import { Chess } from 'chess.js'

export const DIAGNOSIS_SCHEMA_VERSION = 'diagnosis-v3'
// The current production Maia-3 identity. Fixtures and mocks reuse it as a
// realistic sample; the validator no longer compares against it (F10): a
// later pin bump must not flip published documents to invalid at read time.
// Modeled blocks instead require a complete recorded identity — a real
// model_version and a 64-hex behavior profile — consistent document-wide.
export const MAIA3_MODEL_VERSION = 'maia3-79m@a107d6ceb7b298cb04ae1da4edffe2939858b894'
export const MAIA3_BEHAVIOR_PROFILE_HASH = 'ce571773ad338a66658eb8ac4d64646a7bf481d202f9bcfe1bff271ae0926c72'

// The aspect sections, in report order (openings → tactics → strategy →
// endgame → time). The order is part of the v3 contract's UX, not a styling
// choice. ``time`` (D2) is a first-class aspect: the clock as its own leak.
export const ASPECT_ORDER = ['openings', 'tactics', 'strategy', 'endgame', 'time']
const ASPECT_SET = new Set(ASPECT_ORDER)
const SECTION_STATES = new Set(['diagnosis', 'observations_only', 'insufficient_data'])

export const DIAGNOSIS_STATES = new Set([
  'complete',
  'insufficient_evidence',
  'partial',
])

const DELIVERY_STATES = new Set([
  ...DIAGNOSIS_STATES,
  'stale',
  'failed',
])
const DELIVERY_ACTIONS = new Set([
  'none',
  'check_fresh_games',
  'play_more_games',
  'observe_refresh',
  'retry_refresh',
  'retry_report',
])
const REFRESH_OUTCOMES = new Set(['new_games', 'no_new_games', 'reclassified', 'retried'])

export const COACHING_ROLES = new Set([
  'missed_weapon',
  'conversion_leak',
  'defensive_concession',
  'saving_resource_missed',
  'unresolved_critical',
])
const COACHING_ARCS = new Set([...COACHING_ROLES, 'mixed'])

const OPPORTUNITY_RATE_STATUSES = new Set([
  'available',
  'mixed_enumerator_versions',
  'insufficient_sample',
])
const FINDABILITY_LEVELS = new Set(['natural', 'teachable', 'computer_only', 'unmodeled'])
const HUMAN_MODEL_SOURCES = new Set(['maia3', 'none'])
const HUMAN_MODEL_TRANSPORTS = new Set(['batch', 'single_predict', 'cache', 'none'])
const DIFFICULTY_LEVELS = new Set(['light', 'guided', 'full', 'unmodeled'])
const HINT_POLICIES = new Set(['none', 'one_hint', 'guided_steps', 'unmodeled'])
const HUMAN_RANKING_POLICY = 'practice-hard-gate_then_human-teachability_then_j1-order'
const MIN_RECOMMENDATION_GAMES = 3
const MIN_PRACTICE_READY_GAMES = 3
const IDENTITY_ARTIFACT_VERSION = 'diagnosis-archetype-v2'
const EVIDENCE_PROOF_DETECTORS = {
  checks_first_missed_mate: {
    version: 'checks-first-missed-mate-v1', mechanism: 'missed_mate', principle: 'calculation', playedPlies: 1, safePlies: 4,
  },
  piece_safety_loose_material: {
    version: 'piece-safety-loose-material-v1', mechanism: 'hanging_piece', principle: 'piece_safety', playedPlies: 3, safePlies: 3,
  },
  finish_exchange_sequence: {
    version: 'finish-exchange-sequence-v1', mechanism: 'exchange_sequence_loss', principle: 'finish_the_exchange', playedPlies: 2, safePlies: 2,
  },
  kpk_exact_key_squares: {
    version: 'kpk-exact-key-squares-v1', mechanism: 'kpk_key_squares', principle: 'king_pawn_opposition', playedPlies: 2, safePlies: 2,
  },
  delayed_king_safety_central_check: {
    version: 'delayed-king-safety-central-check-v1', mechanism: 'central_king_exposure', principle: 'delayed_king_safety', playedPlies: 4, safePlies: 1,
  },
  two_target_fork: {
    version: 'two-target-fork-v1', mechanism: 'fork', principle: 'calculation', playedPlies: 4, safePlies: 4,
  },
  back_rank_geometry: {
    version: 'back-rank-geometry-v1', mechanism: 'back_rank', principle: 'calculation', playedPlies: 4, safePlies: 1,
  },
}
const KPK_WDL = new Set(['win', 'draw', 'loss'])
const KPK_TERMINALS = new Set([
  'promotion_checkmate',
  'promotion_stalemate',
  'insufficient_material_underpromotion',
  'promoted_piece_immediately_capturable',
  'winning_major_piece_promotion',
  'pawn_captured_insufficient',
  'repetition_cycle',
  'checkmate',
  'stalemate',
])
const ARCHETYPE_CATALOG = {
  generous_winner: {
    label: 'The Generous Winner',
    description: 'You were winning in most of your losses. The positions were there — you just need to close.',
    practice_bridge: 'Your weapon is the conversion: learn to press the advantage.',
  },
  speed_blunderer: {
    label: 'The Fast Mover',
    description: "Many of this window's costly decisions followed moves played in under fifteen seconds.",
    practice_bridge: 'Your weapon is the pause: 15 seconds on any capture.',
  },
  endgame_collapser: {
    label: 'The Long-Game Improver',
    description: "More of this window's measured reversals arrived in endgames than in openings or middlegames.",
    practice_bridge: 'Your weapon is the technique: one clean conversion per session.',
  },
  opening_explorer: {
    label: 'The Opening Explorer',
    description: 'The damage starts early. One familiar setup keeps handing the opponent the steering wheel.',
    practice_bridge: 'Your weapon is the prep: one line, known cold.',
  },
  tilt_prone: {
    label: 'The Queue Re-Enterer',
    description: 'After a loss, another game often starts within sixty seconds.',
    practice_bridge: 'Your weapon is the reset: one breath between games.',
  },
  fork_magnet: {
    label: 'The Fork Magnet',
    description: "Your pieces keep lining up on the same fork geometry. The tactic isn't rare; it is waving.",
    practice_bridge: 'Your weapon is the scan: king, queen, rook, same color complex.',
  },
  back_rank_lottery: {
    label: 'The Back-Rank Lottery',
    description: 'The escape square keeps arriving one move late. Your king needs a little air before the fireworks.',
    practice_bridge: 'Your weapon is luft: make the quiet square before the crisis.',
  },
  loose_piece_collector: {
    label: 'The Loose-Piece Collector',
    description: 'Unprotected pieces are deciding games before the attack even looks dramatic.',
    practice_bridge: 'Your weapon is the census: count loose pieces before captures.',
  },
  steady_improver: {
    label: 'The Steady Improver',
    description: 'No single supported pattern dominates this report yet. The honest identity is still taking shape.',
    practice_bridge: 'Your weapon is consistency: keep showing up.',
  },
}

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function hasOwn(value, key) {
  return Object.hasOwn(value, key)
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0
}

function isNonNegativeInteger(value) {
  return Number.isInteger(value) && value >= 0
}

function pushRequiredString(errors, value, path) {
  if (!isNonEmptyString(value)) errors.push(`${path} must be a non-empty string`)
}

function pushNullableString(errors, value, path) {
  if (value != null && typeof value !== 'string') errors.push(`${path} must be a string or null`)
}

function pushNullableSha256(errors, value, path) {
  if (value != null && !/^[0-9a-f]{64}$/.test(String(value))) {
    errors.push(`${path} must be a lowercase SHA-256 or null`)
  }
}

function pushBoolean(errors, value, path) {
  if (typeof value !== 'boolean') errors.push(`${path} must be a boolean`)
}

function pushEnum(errors, value, allowed, path) {
  if (!allowed.has(value)) errors.push(`${path} is not a supported diagnosis-v3 value`)
}

function pushBoundedNumber(errors, value, path, { nullable = false, min = 0, max = Infinity, exclusiveMin = false } = {}) {
  if (nullable && value == null) return
  const belowMin = exclusiveMin ? value <= min : value < min
  if (!Number.isFinite(value) || belowMin || value > max) {
    const nullSuffix = nullable ? ' or null' : ''
    errors.push(`${path} must be a number from ${exclusiveMin ? `above ${min}` : min} to ${max}${nullSuffix}`)
  }
}

function pushInteger(errors, value, path, { nullable = false, min = -Infinity, max = Infinity } = {}) {
  if (nullable && value == null) return
  if (!Number.isInteger(value) || value < min || value > max) {
    const nullSuffix = nullable ? ' or null' : ''
    errors.push(`${path} must be an integer from ${min} to ${max}${nullSuffix}`)
  }
}

function validateShape(value, path, errors, required, optional = []) {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`)
    return false
  }
  const allowed = new Set([...required, ...optional])
  for (const key of required) {
    if (!hasOwn(value, key)) errors.push(`${path}.${key} is required`)
  }
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) errors.push(`${path}.${key} is not allowed by diagnosis-v3`)
  }
  return true
}

function validateStringArray(value, path, errors, { nonEmpty = true, unique = false } = {}) {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`)
    return false
  }
  value.forEach((item, index) => {
    const valid = nonEmpty ? isNonEmptyString(item) : typeof item === 'string'
    if (!valid) errors.push(`${path}[${index}] must be ${nonEmpty ? 'a non-empty string' : 'a string'}`)
  })
  if (unique && new Set(value).size !== value.length) errors.push(`${path} must contain unique IDs`)
  return true
}

function sameStringSets(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right)) return false
  const leftSet = new Set(left)
  const rightSet = new Set(right)
  return leftSet.size === rightSet.size && [...leftSet].every((item) => rightSet.has(item))
}

/**
 * A renderability check, deliberately separate from whole-report validation.
 * One malformed or absent board must degrade to textual evidence instead of
 * suppressing an otherwise valid cross-game diagnosis.
 */
export function isRenderableFen(fen) {
  if (!isNonEmptyString(fen)) return false
  try {
    new Chess(fen)
    return true
  } catch {
    return false
  }
}

function legalUciForValue(fen, value) {
  if (!isNonEmptyString(fen) || !isNonEmptyString(value)) return null
  try {
    const game = new Chess(fen)
    const legalMoves = game.moves({ verbose: true })
    const raw = value.trim()
    const direct = legalMoves.find((move) => (
      `${move.from}${move.to}${move.promotion || ''}` === raw.toLowerCase()
    ))
    const san = direct || legalMoves.find((move) => move.san === raw)
    return san ? `${san.from}${san.to}${san.promotion || ''}` : null
  } catch {
    return null
  }
}

/**
 * Return an available, internally consistent opportunity rate or null.
 * Disabled rates remain valid report evidence, but visible consumers must not
 * invent a calibrated percentage from their raw numerator and denominator.
 */
export function validatedOpportunity(value) {
  if (!isRecord(value) || value.rate_status !== 'available') return null
  const { miss_count: misses, found_count: found, total, miss_rate: rate } = value
  if (![misses, found, total].every(isNonNegativeInteger)) return null
  if (total <= 0 || misses + found !== total) return null
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) return null
  if (Math.abs(rate - (misses / total)) > 0.001) return null
  return { misses, found, total, rate }
}

function validateRecurrence(recurrence, path, errors) {
  const required = ['game_count', 'position_count', 'window_games', 'game_ids', 'event_ids']
  if (!validateShape(recurrence, path, errors, required)) return

  for (const key of ['game_count', 'position_count', 'window_games']) {
    pushInteger(errors, recurrence[key], `${path}.${key}`, { min: 0 })
  }
  validateStringArray(recurrence.game_ids, `${path}.game_ids`, errors, { unique: true })
  validateStringArray(recurrence.event_ids, `${path}.event_ids`, errors, { unique: true })

  if (Array.isArray(recurrence.game_ids) && new Set(recurrence.game_ids).size !== recurrence.game_count) {
    errors.push(`${path}.game_count must equal the unique source game set`)
  }
  if (Array.isArray(recurrence.event_ids) && new Set(recurrence.event_ids).size !== recurrence.position_count) {
    errors.push(`${path}.position_count must equal the unique source event set`)
  }
  if (
    isNonNegativeInteger(recurrence.game_count)
    && isNonNegativeInteger(recurrence.position_count)
    && recurrence.position_count < recurrence.game_count
  ) {
    errors.push(`${path}.position_count cannot be smaller than game_count`)
  }
  if (
    isNonNegativeInteger(recurrence.game_count)
    && isNonNegativeInteger(recurrence.window_games)
    && recurrence.game_count > recurrence.window_games
  ) {
    errors.push(`${path}.game_count cannot exceed window_games`)
  }
}

function validateImpact(impact, path, errors) {
  const required = ['unit', 'total_expected_score_loss_pp', 'avg_expected_score_loss_pp']
  const optional = ['ranking_capped_loss_pp', 'per_game_cap_pp']
  if (!validateShape(impact, path, errors, required, optional)) return
  if (impact.unit !== 'expected_score_percentage_points') {
    errors.push(`${path}.unit must be expected_score_percentage_points`)
  }
  pushBoundedNumber(errors, impact.total_expected_score_loss_pp, `${path}.total_expected_score_loss_pp`)
  pushBoundedNumber(errors, impact.avg_expected_score_loss_pp, `${path}.avg_expected_score_loss_pp`)
  if (hasOwn(impact, 'ranking_capped_loss_pp')) {
    pushBoundedNumber(errors, impact.ranking_capped_loss_pp, `${path}.ranking_capped_loss_pp`, { nullable: true })
  }
  if (hasOwn(impact, 'per_game_cap_pp')) {
    pushBoundedNumber(errors, impact.per_game_cap_pp, `${path}.per_game_cap_pp`, {
      nullable: true,
      exclusiveMin: true,
    })
  }
}

function validateOpportunitySummary(opportunity, path, errors) {
  const required = [
    'miss_count',
    'found_count',
    'total',
    'miss_rate',
    'ambiguous_count',
    'not_eligible_count',
    'opportunity_ids',
    'assessment_ids',
    'found_opportunity_ids',
    'missed_opportunity_ids',
    'enumerator_version',
    'rate_status',
  ]
  if (!validateShape(opportunity, path, errors, required)) return

  for (const key of ['miss_count', 'found_count', 'total', 'ambiguous_count', 'not_eligible_count']) {
    pushInteger(errors, opportunity[key], `${path}.${key}`, { min: 0 })
  }
  pushBoundedNumber(errors, opportunity.miss_rate, `${path}.miss_rate`, { nullable: true, max: 1 })
  validateStringArray(opportunity.opportunity_ids, `${path}.opportunity_ids`, errors, { unique: true })
  validateStringArray(opportunity.assessment_ids, `${path}.assessment_ids`, errors, { unique: true })
  validateStringArray(opportunity.found_opportunity_ids, `${path}.found_opportunity_ids`, errors, { unique: true })
  validateStringArray(opportunity.missed_opportunity_ids, `${path}.missed_opportunity_ids`, errors, { unique: true })
  pushNullableString(errors, opportunity.enumerator_version, `${path}.enumerator_version`)
  pushEnum(errors, opportunity.rate_status, OPPORTUNITY_RATE_STATUSES, `${path}.rate_status`)

  if (
    isNonNegativeInteger(opportunity.miss_count)
    && isNonNegativeInteger(opportunity.found_count)
    && isNonNegativeInteger(opportunity.total)
    && opportunity.miss_count + opportunity.found_count !== opportunity.total
  ) {
    errors.push(`${path} counts and miss_rate contradict each other`)
  }
  if (
    Array.isArray(opportunity.found_opportunity_ids)
    && new Set(opportunity.found_opportunity_ids).size !== opportunity.found_count
  ) {
    errors.push(`${path}.found_count must equal its exact ID set`)
  }
  if (
    Array.isArray(opportunity.missed_opportunity_ids)
    && new Set(opportunity.missed_opportunity_ids).size !== opportunity.miss_count
  ) {
    errors.push(`${path}.miss_count must equal its exact ID set`)
  }
  if (
    Array.isArray(opportunity.opportunity_ids)
    && Array.isArray(opportunity.found_opportunity_ids)
    && Array.isArray(opportunity.missed_opportunity_ids)
  ) {
    const denominatorIds = [
      ...new Set([...opportunity.found_opportunity_ids, ...opportunity.missed_opportunity_ids]),
    ]
    if (!sameStringSets(opportunity.opportunity_ids, denominatorIds)) {
      errors.push(`${path}.opportunity_ids must be exactly found ∪ missed IDs`)
    }
  }
  if (
    Array.isArray(opportunity.assessment_ids)
    && Array.isArray(opportunity.opportunity_ids)
    && opportunity.opportunity_ids.some((id) => !opportunity.assessment_ids.includes(id))
  ) {
    errors.push(`${path}.assessment_ids must contain every denominator ID`)
  }
  if (
    Array.isArray(opportunity.assessment_ids)
    && isNonNegativeInteger(opportunity.total)
    && isNonNegativeInteger(opportunity.ambiguous_count)
    && isNonNegativeInteger(opportunity.not_eligible_count)
    && new Set(opportunity.assessment_ids).size
      !== opportunity.total + opportunity.ambiguous_count + opportunity.not_eligible_count
  ) {
    errors.push(`${path}.assessment_ids must exactly cover every enumerator outcome`)
  }

  if (opportunity.miss_rate == null) {
    if (opportunity.rate_status === 'available') {
      errors.push(`${path}.miss_rate is required when rate_status is available`)
    }
  } else if (
    opportunity.rate_status !== 'available'
    || !opportunity.total
    || !Number.isFinite(opportunity.miss_rate)
    || Math.abs(opportunity.miss_rate - (opportunity.miss_count / opportunity.total)) > 0.001
  ) {
    errors.push(`${path} counts and miss_rate contradict each other`)
  }
}

function validateDifficultyContext(difficulty, path, errors) {
  const required = [
    'source',
    'level',
    'hint_policy',
    'target_success_probability',
    'confidence',
    'model_version',
  ]
  if (!validateShape(difficulty, path, errors, required, ['behavior_profile_hash'])) return
  pushEnum(errors, difficulty.source, HUMAN_MODEL_SOURCES, `${path}.source`)
  pushEnum(errors, difficulty.level, DIFFICULTY_LEVELS, `${path}.level`)
  pushEnum(errors, difficulty.hint_policy, HINT_POLICIES, `${path}.hint_policy`)
  pushBoundedNumber(errors, difficulty.target_success_probability, `${path}.target_success_probability`, {
    nullable: true,
    max: 1,
  })
  pushBoundedNumber(errors, difficulty.confidence, `${path}.confidence`, { max: 1 })
  pushRequiredString(errors, difficulty.model_version, `${path}.model_version`)
  if (hasOwn(difficulty, 'behavior_profile_hash')) {
    pushNullableSha256(errors, difficulty.behavior_profile_hash, `${path}.behavior_profile_hash`)
  }
  if (difficulty.source === 'none' && (
    difficulty.level !== 'unmodeled'
    || difficulty.hint_policy !== 'unmodeled'
    || difficulty.target_success_probability != null
    || difficulty.confidence !== 0
    || difficulty.model_version !== 'unmodeled'
    || difficulty.behavior_profile_hash != null
  )) {
    errors.push(`${path} unmodeled difficulty cannot contain model-derived claims`)
  }
  // F10: the pinned-profile equality check is writer-side. Read-time
  // validation requires a complete recorded identity, not a specific pin.
  if (difficulty.source === 'maia3' && (
    difficulty.level === 'unmodeled'
    || difficulty.hint_policy === 'unmodeled'
    || difficulty.target_success_probability == null
    || difficulty.model_version === 'unmodeled'
    || !/^[0-9a-f]{64}$/.test(String(difficulty.behavior_profile_hash || ''))
  )) {
    errors.push(`${path} Maia-3 difficulty requires a complete recorded model identity`)
  }
}

function validatePracticeMaterial(material, path, errors) {
  if (material == null) return
  const required = [
    'schema_version',
    'material_id',
    'material_version',
    'material_type',
    'verification',
    'source_example_ids',
    'content_digest',
  ]
  if (!validateShape(material, path, errors, required)) return
  if (material.schema_version !== 'practice-material-v1') {
    errors.push(`${path}.schema_version must be practice-material-v1`)
  }
  pushRequiredString(errors, material.material_id, `${path}.material_id`)
  if (!/^[0-9a-f]{24}$/.test(String(material.material_id || ''))) {
    errors.push(`${path}.material_id must be a content-derived 24-character hex ID`)
  }
  if (material.material_version !== 'own-game-board-drill-v1') {
    errors.push(`${path}.material_version must be own-game-board-drill-v1`)
  }
  if (material.material_type !== 'own_game_board_drill') {
    errors.push(`${path}.material_type must be own_game_board_drill`)
  }
  if (material.verification !== 'server_legal_move_v1') {
    errors.push(`${path}.verification must be server_legal_move_v1`)
  }
  validateStringArray(material.source_example_ids, `${path}.source_example_ids`, errors, {
    nonEmpty: true,
  })
  if (
    Array.isArray(material.source_example_ids)
    && new Set(material.source_example_ids).size !== material.source_example_ids.length
  ) {
    errors.push(`${path}.source_example_ids must be unique`)
  }
  if (!/^[0-9a-f]{64}$/.test(String(material.content_digest || ''))) {
    errors.push(`${path}.content_digest must be a lowercase SHA-256 digest`)
  }
}

function validatePractice(practice, path, errors) {
  const required = ['ready', 'action_label', 'objective', 'reason', 'material']
  const optional = ['difficulty_context']
  if (!validateShape(practice, path, errors, required, optional)) return
  pushBoolean(errors, practice.ready, `${path}.ready`)
  pushNullableString(errors, practice.action_label, `${path}.action_label`)
  pushNullableString(errors, practice.objective, `${path}.objective`)
  pushNullableString(errors, practice.reason, `${path}.reason`)
  validatePracticeMaterial(practice.material, `${path}.material`, errors)
  if (practice.ready) {
    pushRequiredString(errors, practice.action_label, `${path}.action_label`)
    pushRequiredString(errors, practice.objective, `${path}.objective`)
    if (!isRecord(practice.material)) {
      errors.push(`${path}.material must identify immutable compiled practice`)
    }
  } else if (practice.ready === false) {
    pushRequiredString(errors, practice.reason, `${path}.reason`)
    if (practice.action_label != null || practice.objective != null || practice.material != null) {
      errors.push(`${path} cannot advertise an action, objective, or material while practice is locked`)
    }
  }
  if (hasOwn(practice, 'difficulty_context') && practice.difficulty_context != null) {
    validateDifficultyContext(practice.difficulty_context, `${path}.difficulty_context`, errors)
  }
}

function validateProofLine(frames, sourceFen, path, errors) {
  if (!Array.isArray(frames) || frames.length === 0) {
    errors.push(`${path} must be a non-empty array`)
    return null
  }
  let game
  try {
    game = new Chess(sourceFen)
  } catch {
    errors.push(`${path} source FEN must be legal`)
    return null
  }
  let root = null
  frames.forEach((frame, index) => {
    const framePath = `${path}[${index}]`
    const required = [
      'ply', 'move_no', 'side', 'san', 'uci', 'fen_before', 'fen_after', 'is_focus',
    ]
    if (!validateShape(frame, framePath, errors, required)) return
    pushInteger(errors, frame.ply, `${framePath}.ply`, { min: 1 })
    pushInteger(errors, frame.move_no, `${framePath}.move_no`, { min: 1 })
    pushEnum(errors, frame.side, new Set(['white', 'black']), `${framePath}.side`)
    pushRequiredString(errors, frame.san, `${framePath}.san`)
    pushRequiredString(errors, frame.uci, `${framePath}.uci`)
    pushRequiredString(errors, frame.fen_before, `${framePath}.fen_before`)
    pushRequiredString(errors, frame.fen_after, `${framePath}.fen_after`)
    pushBoolean(errors, frame.is_focus, `${framePath}.is_focus`)

    const currentFen = game.fen()
    const side = game.turn() === 'w' ? 'white' : 'black'
    const moveNo = Number(currentFen.split(' ')[5])
    const ply = moveNo * 2 - (game.turn() === 'w' ? 1 : 0)
    if (frame.fen_before !== currentFen) errors.push(`${framePath}.fen_before breaks proof continuity`)
    if (frame.side !== side || frame.move_no !== moveNo || frame.ply !== ply) {
      errors.push(`${framePath} metadata contradicts its board`)
    }
    if (frame.is_focus !== (index === 0)) {
      errors.push(`${framePath}.is_focus must identify only the branch root`)
    }
    if (!/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(String(frame.uci || ''))) {
      errors.push(`${framePath}.uci must be a complete UCI move`)
      return
    }
    try {
      const move = game.move({
        from: frame.uci.slice(0, 2),
        to: frame.uci.slice(2, 4),
        promotion: frame.uci.slice(4, 5) || undefined,
      })
      if (!move) throw new Error('illegal')
      if (move.san !== frame.san) errors.push(`${framePath}.san contradicts its UCI move`)
      if (index === 0) root = `${move.from}${move.to}${move.promotion || ''}`
      if (frame.fen_after !== game.fen()) errors.push(`${framePath}.fen_after contradicts its move`)
    } catch {
      errors.push(`${framePath}.uci must be legal in fen_before`)
    }
  })
  return { root, length: frames.length, terminal: game.isGameOver() }
}

function expectedProofSummary(proof) {
  const playedRoot = proof.played_line?.[0]?.san
  const safeRoot = proof.safe_line?.[0]?.san
  if (!playedRoot || !safeRoot) return null
  if (proof.mechanism === 'exchange_sequence_loss' && Number.isInteger(proof.proven_net_loss_cp)) {
    const played = proof.played_line.map((frame) => frame.san).join(' ')
    return `${played} lets the recapture tree settle ${proof.proven_net_loss_cp / 100} pawns of material down. ${safeRoot} avoids that exchange.`
  }
  if (proof.mechanism === 'hanging_piece') {
    return `After ${playedRoot}, a non-pawn remains available to be won. ${safeRoot} avoids the comparable material loss.`
  }
  if (proof.mechanism === 'missed_mate') {
    return `${safeRoot} begins the forced mate; ${playedRoot} lets it go.`
  }
  if (proof.mechanism === 'fork') {
    return `${safeRoot} creates the proven two-target fork; ${playedRoot} misses it.`
  }
  if (proof.mechanism === 'back_rank') {
    return `${playedRoot} permits the proven back-rank finish. ${safeRoot} prevents it.`
  }
  if (proof.mechanism === 'kpk_key_squares' && isRecord(proof.kpk_exact)) {
    return `${playedRoot} changes the exact K+P versus K result from ${proof.kpk_exact.initial_wdl} to ${proof.kpk_exact.played_wdl}. ${safeRoot} preserves the ${proof.kpk_exact.initial_wdl}.`
  }
  if (proof.mechanism === 'central_king_exposure') {
    const check = boundedCentralKingCheck(proof.played_line)
    const safeCastle = castlingRoot(proof.safe_line)
    if (check && safeCastle) {
      return `${playedRoot} leaves the king on ${check.kingSquare}; ${check.san} checks it inside the bounded line. ${safeRoot} castles in the exact position.`
    }
  }
  return `The detector proves the difference between ${playedRoot} and ${safeRoot} on this exact board.`
}

function boundedCentralKingCheck(frames) {
  const sourceFen = frames?.[0]?.fen_before
  if (!isNonEmptyString(sourceFen) || !Array.isArray(frames)) return null
  try {
    const game = new Chess(sourceFen)
    const subject = game.turn()
    const home = subject === 'w' ? 'e1' : 'e8'
    for (let index = 0; index < Math.min(frames.length, 4); index += 1) {
      const mover = game.turn()
      const frame = frames[index]
      const move = game.move({
        from: frame.uci?.slice(0, 2),
        to: frame.uci?.slice(2, 4),
        promotion: frame.uci?.slice(4, 5) || undefined,
      })
      if (!move) return null
      const king = game.get(home)
      if (
        mover !== subject
        && game.turn() === subject
        && game.isCheck()
        && king?.type === 'k'
        && king?.color === subject
      ) {
        return { san: move.san, uci: `${move.from}${move.to}${move.promotion || ''}`, kingSquare: home }
      }
    }
  } catch {
    return null
  }
  return null
}

function castlingRoot(frames) {
  const sourceFen = frames?.[0]?.fen_before
  const uci = frames?.[0]?.uci
  if (!isNonEmptyString(sourceFen) || !isNonEmptyString(uci)) return null
  try {
    const game = new Chess(sourceFen)
    const move = game.move({
      from: uci.slice(0, 2),
      to: uci.slice(2, 4),
      promotion: uci.slice(4, 5) || undefined,
    })
    return move && (move.isKingsideCastle() || move.isQueensideCastle()) ? move : null
  } catch {
    return null
  }
}

function validateCentralKingExposure(proof, example, path, errors) {
  const playedCastle = castlingRoot(proof.played_line)
  const safeCastle = castlingRoot(proof.safe_line)
  const centralCheck = boundedCentralKingCheck(proof.played_line)
  if (playedCastle) errors.push(`${path}.played_line cannot begin with castling`)
  if (!safeCastle) errors.push(`${path}.safe_line must begin with exact acceptable castling`)
  if (!centralCheck) errors.push(`${path}.played_line must check the still-central king`)
  if (!Number.isFinite(example.expected_score_loss_pp) || example.expected_score_loss_pp < 10) {
    errors.push(`${path} requires the canonical expected-score loss floor`)
  }

  try {
    const game = new Chess(example.fen_before)
    const pieces = game.board().flat().filter(Boolean)
    const queens = new Set(pieces.filter((piece) => piece.type === 'q').map((piece) => piece.color))
    if (Number(example.fen_before.split(' ')[5]) > 12 || !queens.has('w') || !queens.has('b')) {
      errors.push(`${path} must stay inside the bounded queens-on opening domain`)
    }
  } catch {
    // Source-board validation reports the malformed FEN separately.
  }

  const opening = example.context?.opening
  const expectedColor = example.orientation
  const openingKeys = ['eco_code', 'eco_family', 'opening_name', 'variation_name', 'eco_url']
  if (
    example.context?.player_color !== expectedColor
    || !isRecord(opening)
    || !sameStringSets(Object.keys(opening), openingKeys)
  ) {
    errors.push(`${path} requires exact subject and opening identity`)
  } else if (
    opening.eco_code != null
    && (
      !/^[A-E][0-9]{2}$/.test(opening.eco_code)
      || opening.eco_family !== opening.eco_code[0]
    )
  ) {
    errors.push(`${path} ECO identity is internally inconsistent`)
  }
}

function validateKpkTerminal(terminal, expectedWdl, path, errors) {
  const required = ['kind', 'wdl', 'proof_complete']
  if (!validateShape(terminal, path, errors, required, ['cycle_start_ply'])) return
  pushEnum(errors, terminal.kind, KPK_TERMINALS, `${path}.kind`)
  pushEnum(errors, terminal.wdl, KPK_WDL, `${path}.wdl`)
  if (terminal.proof_complete !== true) errors.push(`${path}.proof_complete must be true`)
  if (terminal.kind === 'repetition_cycle') {
    pushInteger(errors, terminal.cycle_start_ply, `${path}.cycle_start_ply`, { min: 0 })
  } else if (terminal.cycle_start_ply != null) {
    errors.push(`${path}.cycle_start_ply is only valid for a repetition cycle`)
  }
  if (terminal.wdl !== expectedWdl) errors.push(`${path}.wdl must match its exact branch result`)
}

function validateExactKpkProof(kpk, example, played, safe, path, errors) {
  const required = [
    'solver_version',
    'initial_wdl',
    'played_wdl',
    'side_to_move',
    'pawn_color',
    'pawn_square',
    'halfmove_clock',
    'repetition_history_status',
    'decisive_distance_plies',
    'fifty_move_safe',
    'optimal_root_uci',
    'acceptable_root_uci',
    'played_root_uci',
    'legal_root_outcomes',
    'played_terminal',
    'safe_terminal',
  ]
  if (!validateShape(kpk, path, errors, required)) return
  if (kpk.solver_version !== 'kpk-retrograde-wdl-v1') {
    errors.push(`${path}.solver_version must name the active exact KPK solver`)
  }
  pushEnum(errors, kpk.initial_wdl, KPK_WDL, `${path}.initial_wdl`)
  pushEnum(errors, kpk.played_wdl, KPK_WDL, `${path}.played_wdl`)
  pushEnum(errors, kpk.side_to_move, new Set(['white', 'black']), `${path}.side_to_move`)
  pushEnum(errors, kpk.pawn_color, new Set(['white', 'black']), `${path}.pawn_color`)
  if (!/^[a-h][1-8]$/.test(String(kpk.pawn_square || ''))) {
    errors.push(`${path}.pawn_square must be a board square`)
  }
  if (kpk.halfmove_clock !== 0) errors.push(`${path}.halfmove_clock must prove a clean reset`)
  if (kpk.repetition_history_status !== 'clean_irreversible_reset') {
    errors.push(`${path}.repetition_history_status must prove a clean reset`)
  }
  pushInteger(errors, kpk.decisive_distance_plies, `${path}.decisive_distance_plies`, { nullable: true, min: 0 })
  if (kpk.fifty_move_safe !== true) errors.push(`${path}.fifty_move_safe must be true`)

  const ranks = { loss: 0, draw: 1, win: 2 }
  if (ranks[kpk.played_wdl] >= ranks[kpk.initial_wdl]) {
    errors.push(`${path} must prove that the played root worsens the exact result`)
  }
  const rootPattern = /^[a-h][1-8][a-h][1-8][qrbn]?$/
  for (const key of ['optimal_root_uci', 'played_root_uci']) {
    if (!rootPattern.test(String(kpk[key] || ''))) errors.push(`${path}.${key} must be a UCI move`)
  }
  validateStringArray(kpk.acceptable_root_uci, `${path}.acceptable_root_uci`, errors, { unique: true })
  if (
    Array.isArray(kpk.acceptable_root_uci)
    && JSON.stringify(kpk.acceptable_root_uci) !== JSON.stringify([...kpk.acceptable_root_uci].sort())
  ) {
    errors.push(`${path}.acceptable_root_uci must be canonical`)
  }

  let legalRoots = []
  let rootSan = new Map()
  try {
    const game = new Chess(example.fen_before)
    legalRoots = game.moves({ verbose: true }).map((move) => `${move.from}${move.to}${move.promotion || ''}`)
    rootSan = new Map(game.moves({ verbose: true }).map((move) => [
      `${move.from}${move.to}${move.promotion || ''}`,
      move.san,
    ]))
    if ((game.turn() === 'w' ? 'white' : 'black') !== kpk.side_to_move) {
      errors.push(`${path}.side_to_move must match the source board`)
    }
  } catch {
    // The evidence-example validator reports the malformed source board.
  }

  if (!Array.isArray(kpk.legal_root_outcomes) || kpk.legal_root_outcomes.length === 0) {
    errors.push(`${path}.legal_root_outcomes must contain every legal root`)
  } else {
    const roots = []
    const accepted = []
    kpk.legal_root_outcomes.forEach((outcome, index) => {
      const outcomePath = `${path}.legal_root_outcomes[${index}]`
      if (!validateShape(outcome, outcomePath, errors, ['san', 'uci', 'wdl', 'zeroing', 'accepted'])) return
      pushRequiredString(errors, outcome.san, `${outcomePath}.san`)
      if (!rootPattern.test(String(outcome.uci || ''))) errors.push(`${outcomePath}.uci must be a UCI move`)
      pushEnum(errors, outcome.wdl, KPK_WDL, `${outcomePath}.wdl`)
      pushBoolean(errors, outcome.zeroing, `${outcomePath}.zeroing`)
      pushBoolean(errors, outcome.accepted, `${outcomePath}.accepted`)
      if (rootSan.has(outcome.uci) && rootSan.get(outcome.uci) !== outcome.san) {
        errors.push(`${outcomePath}.san must match its legal root`)
      }
      if (outcome.accepted !== (outcome.wdl === kpk.initial_wdl)) {
        errors.push(`${outcomePath}.accepted must derive from its exact WDL`)
      }
      roots.push(outcome.uci)
      if (outcome.accepted) accepted.push(outcome.uci)
    })
    if (new Set(roots).size !== roots.length) errors.push(`${path}.legal_root_outcomes must be unique`)
    if (!sameStringSets(roots, legalRoots)) errors.push(`${path}.legal_root_outcomes must cover every legal root`)
    if (JSON.stringify(accepted) !== JSON.stringify(kpk.acceptable_root_uci)) {
      errors.push(`${path}.acceptable_root_uci must equal the accepted root table`)
    }
    const playedOutcome = kpk.legal_root_outcomes.find((outcome) => outcome?.uci === kpk.played_root_uci)
    if (playedOutcome?.wdl !== kpk.played_wdl) {
      errors.push(`${path}.played_wdl must match the played root outcome`)
    }
  }

  const expectedPlayed = legalUciForValue(example.fen_before, example.played_move)
  const expectedEquivalents = Array.isArray(example.equivalent_moves)
    ? example.equivalent_moves.map((move) => legalUciForValue(example.fen_before, move))
    : []
  const expectedBest = legalUciForValue(example.fen_before, example.best_move)
  if (kpk.played_root_uci !== expectedPlayed || kpk.played_root_uci !== played?.root) {
    errors.push(`${path}.played_root_uci must bind the played move and proof branch`)
  }
  if (
    !Array.isArray(kpk.acceptable_root_uci)
    || JSON.stringify(kpk.acceptable_root_uci) !== JSON.stringify(expectedEquivalents)
  ) {
    errors.push(`${path}.acceptable_root_uci must equal the example equivalent set`)
  }
  if (
    kpk.optimal_root_uci !== expectedBest
    || kpk.optimal_root_uci !== safe?.root
    || !kpk.acceptable_root_uci?.includes(kpk.optimal_root_uci)
  ) {
    errors.push(`${path}.optimal_root_uci must bind the safe teaching branch`)
  }
  validateKpkTerminal(kpk.played_terminal, kpk.played_wdl, `${path}.played_terminal`, errors)
  validateKpkTerminal(kpk.safe_terminal, kpk.initial_wdl, `${path}.safe_terminal`, errors)
}

function validateEvidenceProof(proof, example, recommendation, path, errors) {
  if (proof == null) return
  const required = [
    'schema_version',
    'proof_id',
    'detector_id',
    'detector_version',
    'registry_version',
    'principle_library_version',
    'evidence_window_key',
    'mechanism',
    'summary',
    'played_label',
    'safe_label',
    'played_line',
    'safe_line',
    'proven_net_loss_cp',
  ]
  if (!validateShape(proof, path, errors, required, ['kpk_exact'])) return
  if (proof.schema_version !== 'evidence-proof-v1') {
    errors.push(`${path}.schema_version must be evidence-proof-v1`)
  }
  for (const key of [
    'proof_id',
    'detector_id',
    'detector_version',
    'registry_version',
    'principle_library_version',
    'evidence_window_key',
    'mechanism',
    'summary',
  ]) pushRequiredString(errors, proof[key], `${path}.${key}`)
  if (!/^[0-9a-f]{24}$/.test(String(proof.proof_id || ''))) {
    errors.push(`${path}.proof_id must be a content-derived 24-character hex ID`)
  }
  if (proof.played_label !== 'What happened') errors.push(`${path}.played_label is invalid`)
  if (proof.safe_label !== 'What holds') errors.push(`${path}.safe_label is invalid`)
  if (proof.mechanism !== example.mechanism) errors.push(`${path}.mechanism must match the example`)
  if (proof.proven_net_loss_cp != null) {
    pushInteger(errors, proof.proven_net_loss_cp, `${path}.proven_net_loss_cp`, { min: 0 })
  }
  const spec = EVIDENCE_PROOF_DETECTORS[proof.detector_id]
  if (!spec) {
    errors.push(`${path}.detector_id must name an enabled registered detector`)
  } else {
    if (
      proof.detector_version !== spec.version
      || proof.mechanism !== spec.mechanism
      || recommendation.principle_id !== spec.principle
      || recommendation.principle_version !== proof.principle_library_version
    ) {
      errors.push(`${path} provenance contradicts the detector/principle registry`)
    }
  }
  const expectedSummary = expectedProofSummary(proof)
  if (expectedSummary && proof.summary !== expectedSummary) {
    errors.push(`${path}.summary must be derived from its exact branches`)
  }
  if (proof.mechanism === 'exchange_sequence_loss') {
    if (
      !Number.isInteger(proof.proven_net_loss_cp)
      || proof.proven_net_loss_cp < 170
      || proof.proven_net_loss_cp >= 320
    ) {
      errors.push(`${path}.proven_net_loss_cp must be the bounded detector-proven exchange loss`)
    }
  } else if (proof.proven_net_loss_cp != null) {
    errors.push(`${path}.proven_net_loss_cp must be null for a non-exchange proof`)
  }
  const played = validateProofLine(proof.played_line, example.fen_before, `${path}.played_line`, errors)
  const safe = validateProofLine(proof.safe_line, example.fen_before, `${path}.safe_line`, errors)
  if (spec && played && played.length < spec.playedPlies && !played.terminal) {
    errors.push(`${path}.played_line is shorter than its detector evidence shape`)
  }
  if (spec && safe && safe.length < spec.safePlies && !safe.terminal) {
    errors.push(`${path}.safe_line is shorter than its detector evidence shape`)
  }
  const expectedPlayed = legalUciForValue(example.fen_before, example.played_move)
  const equivalents = Array.isArray(example.equivalent_moves)
    ? example.equivalent_moves.map((move) => legalUciForValue(example.fen_before, move))
    : []
  if (played?.root && expectedPlayed && played.root !== expectedPlayed) {
    errors.push(`${path}.played_line root must match played_move`)
  }
  if (safe?.root && !equivalents.includes(safe.root)) {
    errors.push(`${path}.safe_line root must belong to equivalent_moves`)
  }
  if (proof.mechanism === 'kpk_key_squares') {
    if (!isRecord(proof.kpk_exact)) {
      errors.push(`${path}.kpk_exact must contain the exact solver certificate`)
    } else {
      validateExactKpkProof(
        proof.kpk_exact,
        example,
        played,
        safe,
        `${path}.kpk_exact`,
        errors,
      )
    }
  } else if (proof.kpk_exact != null) {
    errors.push(`${path}.kpk_exact is only valid for the exact KPK detector`)
  }
  if (proof.mechanism === 'central_king_exposure') {
    validateCentralKingExposure(proof, example, path, errors)
  }
}

function validateEvidenceExample(example, recommendation, path, errors) {
  const required = [
    'id',
    'game_id',
    'game_url',
    'move_no',
    'fen_before',
    'orientation',
    'played_move',
    'best_move',
    'equivalent_moves',
    'expected_score_loss_pp',
    'title',
    'explanation',
    'coaching_role',
    'mechanism',
    'context',
    'position_context',
    'proof',
  ]
  if (!validateShape(example, path, errors, required)) return
  for (const key of ['id', 'game_id', 'fen_before', 'played_move', 'best_move', 'title', 'explanation']) {
    pushRequiredString(errors, example[key], `${path}.${key}`)
  }
  pushNullableString(errors, example.game_url, `${path}.game_url`)
  pushInteger(errors, example.move_no, `${path}.move_no`, { min: 1 })
  if (example.orientation != null && !['white', 'black'].includes(example.orientation)) {
    errors.push(`${path}.orientation must be white, black, or null`)
  }
  validateStringArray(example.equivalent_moves, `${path}.equivalent_moves`, errors, { nonEmpty: false })
  pushBoundedNumber(errors, example.expected_score_loss_pp, `${path}.expected_score_loss_pp`, {
    nullable: true,
    max: 100,
  })
  pushEnum(errors, example.coaching_role, COACHING_ROLES, `${path}.coaching_role`)
  pushNullableString(errors, example.mechanism, `${path}.mechanism`)
  if (!isRecord(example.context)) errors.push(`${path}.context must be an object`)
  if (!isRecord(example.position_context)) errors.push(`${path}.position_context must be an object`)
  validateEvidenceProof(example.proof, example, recommendation, `${path}.proof`, errors)

  if (isRenderableFen(example.fen_before)) {
    const game = new Chess(example.fen_before)
    const expectedOrientation = game.turn() === 'w' ? 'white' : 'black'
    if (example.orientation != null && example.orientation !== expectedOrientation) {
      errors.push(`${path}.orientation must match the side to move`)
    }
    const played = legalUciForValue(example.fen_before, example.played_move)
    const best = legalUciForValue(example.fen_before, example.best_move)
    if (!played) errors.push(`${path}.played_move must be legal in fen_before`)
    if (!best) errors.push(`${path}.best_move must be legal in fen_before`)
    if (!Array.isArray(example.equivalent_moves) || example.equivalent_moves.length === 0) {
      errors.push(`${path}.equivalent_moves must contain the exact equivalent-move set`)
    } else {
      const equivalents = example.equivalent_moves.map((move) => (
        legalUciForValue(example.fen_before, move)
      ))
      if (equivalents.some((move) => !move)) {
        errors.push(`${path}.equivalent_moves must all be legal in fen_before`)
      }
      if (new Set(equivalents).size !== equivalents.length) {
        errors.push(`${path}.equivalent_moves must be unique after normalization`)
      }
      if (best && !equivalents.includes(best)) {
        errors.push(`${path}.best_move must belong to equivalent_moves`)
      }
      if (
        played
        && equivalents.includes(played)
        && Number.isFinite(example.expected_score_loss_pp)
        && example.expected_score_loss_pp > 0.5
      ) {
        errors.push(`${path} an equivalent played move cannot carry a material expected-score loss`)
      }
    }
  } else if (isNonEmptyString(example.fen_before)) {
    errors.push(`${path}.fen_before must be a legal FEN`)
  }
}

function validateFindability(findability, path, errors) {
  const required = [
    'source',
    'rating_bucket',
    'rating',
    'opponent_rating',
    'rating_source',
    'rating_confidence',
    'time_control',
    'time_control_source',
    'model_version',
    'level',
    'confidence',
    'hard_gate_passed',
    'modeled_example_count',
    'total_example_count',
    'fallback_reason',
    'transport',
  ]
  const optional = [
    'behavior_profile_hash',
    'acceptable_move_probability',
    'acceptable_move_rank',
    'played_move_probability',
    'played_move_rank',
    'probability_gap_to_top',
    'normalized_entropy',
    'rating_sensitivity_pp',
    'legal_move_count',
  ]
  if (!validateShape(findability, path, errors, required, optional)) return

  pushEnum(errors, findability.source, HUMAN_MODEL_SOURCES, `${path}.source`)
  for (const key of ['rating_bucket', 'rating', 'opponent_rating']) {
    pushInteger(errors, findability[key], `${path}.${key}`, { nullable: true })
  }
  pushEnum(errors, findability.rating_source, new Set(['provider_pgn', 'none']), `${path}.rating_source`)
  pushBoundedNumber(errors, findability.rating_confidence, `${path}.rating_confidence`, { max: 1 })
  pushNullableString(errors, findability.time_control, `${path}.time_control`)
  pushEnum(errors, findability.time_control_source, new Set(['provider_pgn', 'none']), `${path}.time_control_source`)
  pushRequiredString(errors, findability.model_version, `${path}.model_version`)
  if (hasOwn(findability, 'behavior_profile_hash')) {
    pushNullableSha256(errors, findability.behavior_profile_hash, `${path}.behavior_profile_hash`)
  }
  pushEnum(errors, findability.level, FINDABILITY_LEVELS, `${path}.level`)
  pushBoundedNumber(errors, findability.confidence, `${path}.confidence`, { max: 1 })
  pushBoolean(errors, findability.hard_gate_passed, `${path}.hard_gate_passed`)
  for (const key of [
    'acceptable_move_probability',
    'played_move_probability',
    'probability_gap_to_top',
    'normalized_entropy',
  ]) {
    if (hasOwn(findability, key)) {
      pushBoundedNumber(errors, findability[key], `${path}.${key}`, { nullable: true, max: 1 })
    }
  }
  for (const key of ['acceptable_move_rank', 'played_move_rank', 'legal_move_count']) {
    if (hasOwn(findability, key)) {
      pushInteger(errors, findability[key], `${path}.${key}`, { nullable: true, min: 1 })
    }
  }
  if (hasOwn(findability, 'rating_sensitivity_pp')) {
    pushBoundedNumber(errors, findability.rating_sensitivity_pp, `${path}.rating_sensitivity_pp`, {
      nullable: true,
      max: 100,
    })
  }
  pushInteger(errors, findability.modeled_example_count, `${path}.modeled_example_count`, { min: 0 })
  pushInteger(errors, findability.total_example_count, `${path}.total_example_count`, { min: 0 })
  pushNullableString(errors, findability.fallback_reason, `${path}.fallback_reason`)
  pushEnum(errors, findability.transport, HUMAN_MODEL_TRANSPORTS, `${path}.transport`)

  if (
    Number.isInteger(findability.modeled_example_count)
    && Number.isInteger(findability.total_example_count)
    && findability.modeled_example_count > findability.total_example_count
  ) {
    errors.push(`${path}.modeled_example_count cannot exceed total_example_count`)
  }
  const modelValues = [
    findability.acceptable_move_probability,
    findability.acceptable_move_rank,
    findability.played_move_probability,
    findability.played_move_rank,
    findability.probability_gap_to_top,
    findability.normalized_entropy,
    findability.rating_sensitivity_pp,
    findability.legal_move_count,
  ]
  if (findability.source === 'none' && (
    findability.hard_gate_passed !== false
    || findability.level !== 'unmodeled'
    || findability.confidence !== 0
    || findability.model_version !== 'unmodeled'
    || findability.behavior_profile_hash != null
    || findability.modeled_example_count !== 0
    || findability.transport !== 'none'
    || !isNonEmptyString(findability.fallback_reason)
    || modelValues.some((value) => value != null)
  )) {
    errors.push(`${path} unmodeled findability cannot contain model-derived claims`)
  }
  // F10: no pinned-constant comparison at read time — completeness instead.
  if (findability.source === 'maia3' && (
    findability.hard_gate_passed !== true
    || findability.level === 'unmodeled'
    || findability.model_version === 'unmodeled'
    || !/^[0-9a-f]{64}$/.test(String(findability.behavior_profile_hash || ''))
    || findability.transport === 'none'
    || findability.fallback_reason != null
    || findability.modeled_example_count === 0
    || findability.modeled_example_count !== findability.total_example_count
    || findability.acceptable_move_probability == null
    || findability.acceptable_move_rank == null
    || findability.legal_move_count == null
  )) {
    errors.push(`${path} Maia-3 findability requires a complete recorded model identity`)
  }
}

function validateHumanModelRanking(ranking, path, errors) {
  const required = ['applied', 'version', 'tier', 'level', 'policy']
  if (!validateShape(ranking, path, errors, required)) return
  pushBoolean(errors, ranking.applied, `${path}.applied`)
  pushRequiredString(errors, ranking.version, `${path}.version`)
  pushInteger(errors, ranking.tier, `${path}.tier`, { min: 0, max: 2 })
  pushEnum(errors, ranking.level, FINDABILITY_LEVELS, `${path}.level`)
  if (ranking.policy !== HUMAN_RANKING_POLICY) {
    errors.push(`${path}.policy must be ${HUMAN_RANKING_POLICY}`)
  }
  if (ranking.applied === false) {
    errors.push(`${path} must be omitted when human-model ranking is not applied`)
  }
}

function validateRecommendation(recommendation, index, path, errors) {
  const required = [
    'id',
    'rank',
    'principle_id',
    'principle_version',
    'ranking_version',
    'enumerator_version',
    'label',
    'category',
    'coaching_role',
    'carry_rule',
    'why_now',
    'recurrence',
    'impact',
    'opportunity',
    'practice',
    'context_enrichments',
    'examples',
  ]
  const optional = [
    'coaching_arc',
    'findability',
    'human_model_ranking',
  ]
  if (!validateShape(recommendation, path, errors, required, optional)) return

  for (const key of [
    'id',
    'principle_id',
    'principle_version',
    'ranking_version',
    'label',
    'category',
    'carry_rule',
    'why_now',
  ]) {
    pushRequiredString(errors, recommendation[key], `${path}.${key}`)
  }
  pushNullableString(errors, recommendation.enumerator_version, `${path}.enumerator_version`)
  if (!Number.isInteger(recommendation.rank) || recommendation.rank !== index + 1) {
    errors.push(`${path}.rank must be contiguous from 1 inside its section`)
  }
  pushEnum(errors, recommendation.coaching_role, COACHING_ROLES, `${path}.coaching_role`)
  if (hasOwn(recommendation, 'coaching_arc') && recommendation.coaching_arc != null) {
    pushEnum(
      errors,
      recommendation.coaching_arc,
      COACHING_ARCS,
      `${path}.coaching_arc`,
    )
  }

  validateRecurrence(recommendation.recurrence, `${path}.recurrence`, errors)
  if (
    isNonNegativeInteger(recommendation.recurrence?.game_count)
    && recommendation.recurrence.game_count < MIN_RECOMMENDATION_GAMES
  ) {
    errors.push(`${path} requires at least three independent source games`)
  }
  validateImpact(recommendation.impact, `${path}.impact`, errors)
  if (
    isRecord(recommendation.recurrence)
    && isRecord(recommendation.impact)
    && isNonNegativeInteger(recommendation.recurrence.position_count)
    && Number.isFinite(recommendation.impact.total_expected_score_loss_pp)
    && Number.isFinite(recommendation.impact.avg_expected_score_loss_pp)
  ) {
    const expectedAverage = recommendation.recurrence.position_count
      ? recommendation.impact.total_expected_score_loss_pp / recommendation.recurrence.position_count
      : 0
    if (Math.abs(recommendation.impact.avg_expected_score_loss_pp - expectedAverage) > 0.011) {
      errors.push(`${path}.impact average must equal total impact / source positions`)
    }
  }
  if (
    Number.isFinite(recommendation.impact?.ranking_capped_loss_pp)
    && Number.isFinite(recommendation.impact?.total_expected_score_loss_pp)
    && recommendation.impact.ranking_capped_loss_pp
      > recommendation.impact.total_expected_score_loss_pp + 0.001
  ) {
    errors.push(`${path}.impact ranking-capped value cannot exceed total impact`)
  }
  if (
    Number.isFinite(recommendation.impact?.ranking_capped_loss_pp)
    && Number.isFinite(recommendation.impact?.per_game_cap_pp)
    && isNonNegativeInteger(recommendation.recurrence?.game_count)
    && recommendation.impact.ranking_capped_loss_pp
      > recommendation.impact.per_game_cap_pp * recommendation.recurrence.game_count + 0.001
  ) {
    errors.push(`${path}.impact ranking-capped value exceeds the declared per-game cap`)
  }
  if (recommendation.opportunity != null) {
    validateOpportunitySummary(recommendation.opportunity, `${path}.opportunity`, errors)
  }
  if (recommendation.opportunity == null && recommendation.enumerator_version != null) {
    errors.push(`${path}.enumerator_version requires opportunity evidence`)
  }
  if (
    recommendation.opportunity != null
    && (
      !isNonEmptyString(recommendation.enumerator_version)
      || recommendation.enumerator_version !== recommendation.opportunity.enumerator_version
    )
  ) {
    errors.push(`${path} recommendation and opportunity must share one complete enumerator`)
  }
  validatePractice(recommendation.practice, `${path}.practice`, errors)
  if (
    recommendation.practice?.ready === true
    && isNonNegativeInteger(recommendation.recurrence?.game_count)
    && recommendation.recurrence.game_count < MIN_PRACTICE_READY_GAMES
  ) {
    errors.push(`${path}.practice-ready recommendation requires three independent games`)
  }
  if (
    recommendation.practice?.ready === true
    && (
      !isRecord(recommendation.opportunity)
      || !isNonEmptyString(recommendation.enumerator_version)
      || !isNonNegativeInteger(recommendation.opportunity?.miss_count)
      || !isNonNegativeInteger(recommendation.recurrence?.game_count)
      || recommendation.opportunity.miss_count < recommendation.recurrence.game_count
    )
  ) {
    errors.push(`${path}.practice-ready recommendation requires a complete matching opportunity handoff`)
  }

  if (!Array.isArray(recommendation.context_enrichments)) {
    errors.push(`${path}.context_enrichments must be an array`)
  } else {
    recommendation.context_enrichments.forEach((item, itemIndex) => {
      validateContextEnrichment(item, `${path}.context_enrichments[${itemIndex}]`, errors)
    })
  }

  if (!Array.isArray(recommendation.examples)) {
    errors.push(`${path}.examples must be an array`)
  } else {
    if (recommendation.examples.length > 3) {
      errors.push(`${path}.examples cannot contain more than three presentation examples`)
    }
    const exampleIds = new Set()
    recommendation.examples.forEach((example, exampleIndex) => {
      validateEvidenceExample(example, recommendation, `${path}.examples[${exampleIndex}]`, errors)
      if (exampleIds.has(example?.id)) {
        errors.push(`${path}.examples[${exampleIndex}].id must be unique inside its recommendation`)
      }
      exampleIds.add(example?.id)
      if (
        isRecord(example)
        && Array.isArray(recommendation.recurrence?.event_ids)
        && !recommendation.recurrence.event_ids.includes(example.id)
      ) {
        errors.push(`${path}.examples[${exampleIndex}].id must be a source decision event`)
      }
    })
    if (recommendation.practice?.ready && recommendation.examples.length === 0) {
      errors.push(`${path}.practice-ready recommendation needs a board example`)
    }
    if (
      recommendation.practice?.ready
      && new Set(recommendation.examples.map((example) => example?.game_id)).size
        !== recommendation.examples.length
    ) {
      errors.push(`${path}.practice-ready evidence must use one exact example per game`)
    }
    if (
      recommendation.practice?.ready
      && recommendation.examples.some((example) => !isRecord(example?.proof))
    ) {
      errors.push(`${path}.practice-ready recommendation needs replayable proof for every example`)
    }
    if (recommendation.practice?.ready && isRecord(recommendation.practice.material)) {
      const exampleIds = recommendation.examples.map((example) => example?.id)
      const materialIds = recommendation.practice.material.source_example_ids
      if (
        !Array.isArray(materialIds)
        || materialIds.length !== exampleIds.length
        || materialIds.some((id, index) => id !== exampleIds[index])
      ) {
        errors.push(`${path}.practice.material source IDs must exactly match presentation evidence`)
      }
    }
  }

  if (hasOwn(recommendation, 'findability') && recommendation.findability != null) {
    validateFindability(recommendation.findability, `${path}.findability`, errors)
  }
  if (hasOwn(recommendation, 'human_model_ranking') && recommendation.human_model_ranking != null) {
    validateHumanModelRanking(recommendation.human_model_ranking, `${path}.human_model_ranking`, errors)
  }
  const difficulty = recommendation.practice?.difficulty_context
  const findability = recommendation.findability
  const ranking = recommendation.human_model_ranking
  if (difficulty != null && (
    !isRecord(findability)
    || difficulty.source !== findability.source
    || difficulty.model_version !== findability.model_version
    || difficulty.behavior_profile_hash !== findability.behavior_profile_hash
  )) {
    errors.push(`${path} difficulty and findability must share one evidence source and behavior identity`)
  }
  if (ranking != null && (
    !isRecord(findability)
    || findability.source !== 'maia3'
    || findability.hard_gate_passed !== true
    || ranking.level !== findability.level
    || ranking.version !== recommendation.ranking_version
  )) {
    errors.push(`${path} human-model ranking requires matching gated Maia-3 evidence`)
  }
}

function validateHeader(header, errors) {
  const path = 'header'
  if (!validateShape(header, path, errors, ['username', 'platform', 'rating'])) return
  pushRequiredString(errors, header.username, `${path}.username`)
  pushRequiredString(errors, header.platform, `${path}.platform`)
  pushInteger(errors, header.rating, `${path}.rating`, { nullable: true })
}

function validateWindow(window, errors) {
  const path = 'window'
  if (!validateShape(window, path, errors, ['games_analyzed', 'wins', 'losses'])) return
  for (const key of ['games_analyzed', 'wins', 'losses']) {
    pushInteger(errors, window[key], `${path}.${key}`, { min: 0 })
  }
  if (
    isNonNegativeInteger(window.games_analyzed)
    && isNonNegativeInteger(window.wins)
    && isNonNegativeInteger(window.losses)
    && window.wins + window.losses > window.games_analyzed
  ) {
    errors.push('window.wins + window.losses cannot exceed games_analyzed')
  }
}

function validateRatingContext(context, errors) {
  const path = 'rating_context'
  if (!validateShape(context, path, errors, ['current_rating', 'source'])) return
  pushInteger(errors, context.current_rating, `${path}.current_rating`, { nullable: true })
  pushEnum(errors, context.source, new Set(['latest_game', 'none']), `${path}.source`)
  if ((context.source === 'latest_game') !== (context.current_rating != null)) {
    errors.push(`${path}.source must match current_rating availability`)
  }
}

function validateHeadline(headline, errors) {
  const path = 'headline_stat'
  if (!validateShape(headline, path, errors, ['was_winning', 'total_losses', 'text'])) return
  pushInteger(errors, headline.was_winning, `${path}.was_winning`, { min: 0 })
  pushInteger(errors, headline.total_losses, `${path}.total_losses`, { min: 0 })
  pushRequiredString(errors, headline.text, `${path}.text`)
  if (
    isNonNegativeInteger(headline.was_winning)
    && isNonNegativeInteger(headline.total_losses)
    && headline.was_winning > headline.total_losses
  ) {
    errors.push(`${path}.was_winning cannot exceed total_losses`)
  }
  if (isNonNegativeInteger(headline.was_winning) && isNonNegativeInteger(headline.total_losses)) {
    const expected = headline.total_losses <= 0
      ? 'No losses in this set — nothing to hunt yet.'
      : `You were winning in ${headline.was_winning} of your last ${headline.total_losses} losses.`
    if (headline.text !== expected) errors.push(`${path}.text must be derived from its exact counts`)
  }
}

function validateIdentityArtifact(artifact, errors) {
  const path = 'identity_artifact'
  const required = [
    'version',
    'key',
    'label',
    'description',
    'rationale',
    'practice_bridge',
    'recommendation_id',
    'source_facts',
  ]
  if (!validateShape(artifact, path, errors, required)) return
  if (artifact.version !== IDENTITY_ARTIFACT_VERSION) {
    errors.push(`${path}.version must be ${IDENTITY_ARTIFACT_VERSION}`)
  }
  for (const key of ['key', 'label', 'description', 'rationale', 'practice_bridge']) {
    pushRequiredString(errors, artifact[key], `${path}.${key}`)
  }
  pushNullableString(errors, artifact.recommendation_id, `${path}.recommendation_id`)
  const catalog = ARCHETYPE_CATALOG[artifact.key]
  if (!catalog) {
    errors.push(`${path}.key is not in the approved catalog`)
  } else if (
    artifact.label !== catalog.label
    || artifact.description !== catalog.description
  ) {
    errors.push(`${path} copy must match its versioned catalog entry`)
  }
  if (!Array.isArray(artifact.source_facts) || artifact.source_facts.length === 0) {
    errors.push(`${path}.source_facts must be a non-empty array`)
  } else {
    const paths = []
    artifact.source_facts.forEach((fact, index) => {
      const factPath = `${path}.source_facts[${index}]`
      if (!validateShape(fact, factPath, errors, ['path', 'value'])) return
      pushRequiredString(errors, fact.path, `${factPath}.path`)
      if (
        fact.value != null
        && !['string', 'number', 'boolean'].includes(typeof fact.value)
      ) {
        errors.push(`${factPath}.value must be a scalar or null`)
      }
      paths.push(fact.path)
    })
    if (new Set(paths).size !== paths.length) {
      errors.push(`${path}.source_facts paths must be unique`)
    }
  }
}

function validateRatingTrajectory(trajectory, errors) {
  const path = 'supporting_facts.rating_trajectory'
  const required = ['source', 'game_count', 'start_rating', 'end_rating', 'change', 'points']
  if (!validateShape(trajectory, path, errors, required)) return
  if (trajectory.source !== 'provider_pgn') errors.push(`${path}.source must be provider_pgn`)
  for (const key of ['game_count', 'start_rating', 'end_rating', 'change']) {
    pushInteger(errors, trajectory[key], `${path}.${key}`, {
      min: key === 'game_count' ? 1 : -Infinity,
    })
  }
  if (!Array.isArray(trajectory.points) || trajectory.points.length === 0) {
    errors.push(`${path}.points must be a non-empty array`)
    return
  }
  const ids = []
  const timestamps = []
  trajectory.points.forEach((point, index) => {
    const pointPath = `${path}.points[${index}]`
    if (!validateShape(point, pointPath, errors, ['game_id', 'rating', 'end_timestamp'])) return
    pushRequiredString(errors, point.game_id, `${pointPath}.game_id`)
    pushInteger(errors, point.rating, `${pointPath}.rating`)
    pushBoundedNumber(errors, point.end_timestamp, `${pointPath}.end_timestamp`)
    ids.push(point.game_id)
    timestamps.push(point.end_timestamp)
  })
  if (trajectory.game_count !== trajectory.points.length) {
    errors.push(`${path}.game_count must equal its exact points`)
  }
  if (new Set(ids).size !== ids.length) errors.push(`${path}.points must have unique game IDs`)
  if (timestamps.some((value, index) => index && value < timestamps[index - 1])) {
    errors.push(`${path}.points must be chronological`)
  }
  if (
    trajectory.start_rating !== trajectory.points[0]?.rating
    || trajectory.end_rating !== trajectory.points.at(-1)?.rating
  ) {
    errors.push(`${path} endpoints must match its exact points`)
  }
  if (trajectory.change !== trajectory.end_rating - trajectory.start_rating) {
    errors.push(`${path}.change must equal end minus start`)
  }
}

function validateOpeningFact(opening, index, errors) {
  const path = `supporting_facts.worst_openings_by_color[${index}]`
  const required = [
    'scope',
    'color',
    'eco_family',
    'eco_code',
    'game_count',
    'score',
    'avg_expected_score_at_move_12',
    'flagged',
  ]
  if (!validateShape(opening, path, errors, required)) return
  pushEnum(errors, opening.scope, new Set(['family', 'variation']), `${path}.scope`)
  pushEnum(errors, opening.color, new Set(['white', 'black']), `${path}.color`)
  pushRequiredString(errors, opening.eco_family, `${path}.eco_family`)
  pushNullableString(errors, opening.eco_code, `${path}.eco_code`)
  pushInteger(errors, opening.game_count, `${path}.game_count`, { min: 1 })
  pushBoundedNumber(errors, opening.score, `${path}.score`, { max: 1 })
  pushBoundedNumber(
    errors,
    opening.avg_expected_score_at_move_12,
    `${path}.avg_expected_score_at_move_12`,
    { nullable: true, max: 100 },
  )
  pushBoolean(errors, opening.flagged, `${path}.flagged`)
  if ((opening.scope === 'variation') !== (opening.eco_code != null)) {
    errors.push(`${path}.scope must match ECO-code availability`)
  }
}

const ENRICHMENT_DIMENSIONS = new Set(['phase', 'clock', 'color', 'advantage_state'])

function validateContextEnrichment(item, path, errors) {
  const required = [
    'dimension',
    'bucket',
    'miss_count',
    'opportunity_count',
    'miss_rate_bucket',
    'miss_rate_outside',
    'miss_rate_overall',
    'absolute_uplift',
    'relative_uplift',
    'confidence_tier',
    'opportunity_ids',
    'copy',
    'policy_version',
  ]
  if (!validateShape(item, path, errors, required)) return
  pushEnum(errors, item.dimension, ENRICHMENT_DIMENSIONS, `${path}.dimension`)
  pushRequiredString(errors, item.bucket, `${path}.bucket`)
  pushInteger(errors, item.miss_count, `${path}.miss_count`, { min: 0 })
  pushInteger(errors, item.opportunity_count, `${path}.opportunity_count`, { min: 0 })
  pushBoundedNumber(errors, item.miss_rate_bucket, `${path}.miss_rate_bucket`, { max: 1 })
  pushBoundedNumber(errors, item.miss_rate_outside, `${path}.miss_rate_outside`, { max: 1 })
  pushBoundedNumber(errors, item.miss_rate_overall, `${path}.miss_rate_overall`, { max: 1 })
  if (!Number.isFinite(item.absolute_uplift)) {
    errors.push(`${path}.absolute_uplift must be a number`)
  }
  if (item.relative_uplift != null && !Number.isFinite(item.relative_uplift)) {
    errors.push(`${path}.relative_uplift must be a number or null`)
  }
  // A fact-tier context claim does not exist; gold-asserting copy is banned.
  if (item.confidence_tier !== 'hypothesis') {
    errors.push(`${path}.confidence_tier must be "hypothesis"`)
  }
  validateStringArray(item.opportunity_ids, `${path}.opportunity_ids`, { unique: true })
  if (
    Array.isArray(item.opportunity_ids)
    && Number.isInteger(item.opportunity_count)
    && item.opportunity_ids.length !== item.opportunity_count
  ) {
    errors.push(`${path}.opportunity_count must equal its exact ID set`)
  }
  if (
    Number.isInteger(item.miss_count)
    && Number.isInteger(item.opportunity_count)
    && item.miss_count > item.opportunity_count
  ) {
    errors.push(`${path}.miss_count cannot exceed opportunity_count`)
  }
  pushRequiredString(errors, item.copy, `${path}.copy`)
  if (item.policy_version !== 'opportunity-context-v1') {
    errors.push(`${path}.policy_version must be "opportunity-context-v1"`)
  }
}

function validateWinningLossRow(row, index, errors) {
  const path = `observations.was_winning_ledger[${index}]`
  const required = [
    'game_id',
    'max_expected_score_pct',
    'collapse_phase',
    'has_display_safe_example',
  ]
  if (!validateShape(row, path, errors, required)) return
  pushRequiredString(errors, row.game_id, `${path}.game_id`)
  pushBoundedNumber(errors, row.max_expected_score_pct, `${path}.max_expected_score_pct`, { max: 100 })
  if (row.collapse_phase != null) {
    pushEnum(errors, row.collapse_phase, new Set(['opening', 'middlegame', 'endgame']), `${path}.collapse_phase`)
  }
  pushBoolean(errors, row.has_display_safe_example, `${path}.has_display_safe_example`)
}

function validateOpeningTableRow(row, path, errors) {
  const required = [
    'scope',
    'color',
    'eco_family',
    'eco_code',
    'game_count',
    'score',
    'avg_expected_score_at_move_12',
    'low_sample',
    'flagged',
  ]
  if (!validateShape(row, path, errors, required)) return
  pushEnum(errors, row.scope, new Set(['family', 'variation']), `${path}.scope`)
  pushEnum(errors, row.color, new Set(['white', 'black']), `${path}.color`)
  pushRequiredString(errors, row.eco_family, `${path}.eco_family`)
  pushNullableString(errors, row.eco_code, `${path}.eco_code`)
  pushInteger(errors, row.game_count, `${path}.game_count`, { min: 0 })
  pushBoundedNumber(errors, row.score, `${path}.score`, { max: 1 })
  pushBoundedNumber(errors, row.avg_expected_score_at_move_12, `${path}.avg_expected_score_at_move_12`, {
    nullable: true,
    max: 100,
  })
  pushBoolean(errors, row.low_sample, `${path}.low_sample`)
  pushBoolean(errors, row.flagged, `${path}.flagged`)
}

function validateExpectedPoints(ledger, errors) {
  const path = 'observations.expected_points'
  const required = [
    'unit',
    'total_loss_pp',
    'classified_event_count',
    'games_with_events',
    'per_principle',
  ]
  if (!validateShape(ledger, path, errors, required)) return
  if (ledger.unit !== 'expected_score_percentage_points') {
    errors.push(`${path}.unit must be expected_score_percentage_points`)
  }
  pushBoundedNumber(errors, ledger.total_loss_pp, `${path}.total_loss_pp`)
  pushInteger(errors, ledger.classified_event_count, `${path}.classified_event_count`, { min: 1 })
  pushInteger(errors, ledger.games_with_events, `${path}.games_with_events`, { min: 1 })
  if (!Array.isArray(ledger.per_principle) || ledger.per_principle.length === 0) {
    errors.push(`${path}.per_principle must be a non-empty array`)
    return
  }
  let rowTotal = 0
  let rowEvents = 0
  let previousLoss = Infinity
  ledger.per_principle.forEach((row, index) => {
    const rowPath = `${path}.per_principle[${index}]`
    if (!validateShape(row, rowPath, errors, ['principle_id', 'label', 'total_loss_pp', 'event_count'])) return
    pushRequiredString(errors, row.principle_id, `${rowPath}.principle_id`)
    pushRequiredString(errors, row.label, `${rowPath}.label`)
    pushBoundedNumber(errors, row.total_loss_pp, `${rowPath}.total_loss_pp`)
    pushInteger(errors, row.event_count, `${rowPath}.event_count`, { min: 1 })
    if (Number.isFinite(row.total_loss_pp)) {
      if (row.total_loss_pp > previousLoss) {
        errors.push(`${path}.per_principle must be sorted by descending cost`)
      }
      previousLoss = row.total_loss_pp
      rowTotal += row.total_loss_pp
    }
    if (Number.isInteger(row.event_count)) rowEvents += row.event_count
  })
  if (
    Number.isFinite(ledger.total_loss_pp)
    && Math.abs(ledger.total_loss_pp - Math.round(rowTotal * 100) / 100) > 0.01
  ) {
    errors.push(`${path}.total_loss_pp must equal the exact per-principle rows`)
  }
  if (
    Number.isInteger(ledger.classified_event_count)
    && ledger.classified_event_count !== rowEvents
  ) {
    errors.push(`${path}.classified_event_count must equal the exact per-principle rows`)
  }
}

function validateObservations(observations, errors) {
  const path = 'observations'
  // observations-v2 (M5.2): opening_table left this register — the openings
  // aspect section is now its single home.
  const required = [
    'register',
    'was_winning_ledger',
    'conversion',
    'collapse_by_phase',
    'time_signals',
    'session_signals',
    'expected_points',
  ]
  if (!validateShape(observations, path, errors, required)) return
  if (observations.register !== 'observations-v2') {
    errors.push(`${path}.register must be observations-v2`)
  }
  if (observations.was_winning_ledger != null) {
    if (!Array.isArray(observations.was_winning_ledger) || observations.was_winning_ledger.length === 0) {
      errors.push(`${path}.was_winning_ledger must be a non-empty array or null`)
    } else {
      observations.was_winning_ledger.forEach((row, index) => validateWinningLossRow(row, index, errors))
      const ids = observations.was_winning_ledger.map((row) => row?.game_id)
      if (new Set(ids).size !== ids.length) {
        errors.push(`${path}.was_winning_ledger must have unique game IDs`)
      }
    }
  }
  if (observations.conversion != null) {
    const conversionPath = `${path}.conversion`
    if (validateShape(observations.conversion, conversionPath, errors, ['winning_positions', 'converted_wins'])) {
      pushInteger(errors, observations.conversion.winning_positions, `${conversionPath}.winning_positions`, { min: 1 })
      pushInteger(errors, observations.conversion.converted_wins, `${conversionPath}.converted_wins`, { min: 0 })
      if (observations.conversion.converted_wins > observations.conversion.winning_positions) {
        errors.push(`${conversionPath}.converted_wins cannot exceed winning_positions`)
      }
    }
  }
  if (observations.collapse_by_phase != null) {
    const collapsePath = `${path}.collapse_by_phase`
    if (validateShape(observations.collapse_by_phase, collapsePath, errors, ['opening', 'middlegame', 'endgame'])) {
      for (const phase of ['opening', 'middlegame', 'endgame']) {
        pushInteger(errors, observations.collapse_by_phase[phase], `${collapsePath}.${phase}`, { min: 0 })
      }
    }
  }
  if (observations.time_signals != null) {
    const timePath = `${path}.time_signals`
    const timeKeys = [
      'avg_time_on_decisive',
      'avg_time_on_normal',
      'fast_blunders_pct',
      'time_pressure_blunders_pct',
    ]
    if (validateShape(observations.time_signals, timePath, errors, timeKeys)) {
      pushBoundedNumber(errors, observations.time_signals.avg_time_on_decisive, `${timePath}.avg_time_on_decisive`, { nullable: true })
      pushBoundedNumber(errors, observations.time_signals.avg_time_on_normal, `${timePath}.avg_time_on_normal`, { nullable: true })
      pushBoundedNumber(errors, observations.time_signals.fast_blunders_pct, `${timePath}.fast_blunders_pct`, { nullable: true, max: 100 })
      pushBoundedNumber(errors, observations.time_signals.time_pressure_blunders_pct, `${timePath}.time_pressure_blunders_pct`, { nullable: true, max: 100 })
    }
  }
  if (observations.session_signals != null) {
    const sessionPath = `${path}.session_signals`
    if (validateShape(observations.session_signals, sessionPath, errors, ['max_loss_streak', 'quick_requeues_after_loss'])) {
      pushInteger(errors, observations.session_signals.max_loss_streak, `${sessionPath}.max_loss_streak`, { min: 0 })
      pushInteger(errors, observations.session_signals.quick_requeues_after_loss, `${sessionPath}.quick_requeues_after_loss`, { min: 0 })
    }
  }
  if (observations.expected_points != null) {
    validateExpectedPoints(observations.expected_points, errors)
  }
}

// --- diagnosis-v3 aspect machinery ------------------------------------------

function validateAspectExpectedPoints(points, path, errors) {
  const required = ['events', 'expected_points_lost_pp', 'games_with_events', 'unit']
  if (!validateShape(points, path, errors, required)) return
  pushInteger(errors, points.events, `${path}.events`, { min: 0 })
  pushBoundedNumber(errors, points.expected_points_lost_pp, `${path}.expected_points_lost_pp`)
  pushInteger(errors, points.games_with_events, `${path}.games_with_events`, { min: 0 })
  if (points.unit !== 'expected_score_percentage_points') {
    errors.push(`${path}.unit must be expected_score_percentage_points`)
  }
}

function validateNoticedInsight(item, aspect, path, errors) {
  const required = [
    'schema_version',
    'aspect',
    'principle_id',
    'claim_tier',
    'recurrence_game_ids',
    'impact',
    'practice',
    'provenance',
    'module_version',
  ]
  if (!validateShape(item, path, errors, required)) return
  if (item.schema_version !== 'noticed-insight-v1') {
    errors.push(`${path}.schema_version must be noticed-insight-v1`)
  }
  pushEnum(errors, item.aspect, ASPECT_SET, `${path}.aspect`)
  if (ASPECT_SET.has(item.aspect) && item.aspect !== aspect) {
    errors.push(`${path}.aspect must belong to its section's aspect`)
  }
  pushRequiredString(errors, item.principle_id, `${path}.principle_id`)
  // A diagnosis-tier noticed claim does not exist: hypothesis is pinned.
  if (item.claim_tier !== 'hypothesis') {
    errors.push(`${path}.claim_tier must be "hypothesis"`)
  }
  if (
    !Array.isArray(item.recurrence_game_ids)
    || item.recurrence_game_ids.length === 0
  ) {
    errors.push(`${path}.recurrence_game_ids must be a non-empty array`)
  } else {
    validateStringArray(item.recurrence_game_ids, `${path}.recurrence_game_ids`, errors, {
      unique: true,
    })
  }
  const impactPath = `${path}.impact`
  if (validateShape(item.impact, impactPath, errors, [
    'expected_points_lost_pp',
    'per_game_cap_pp',
    'unit',
  ])) {
    pushBoundedNumber(errors, item.impact.expected_points_lost_pp, `${impactPath}.expected_points_lost_pp`)
    pushBoundedNumber(errors, item.impact.per_game_cap_pp, `${impactPath}.per_game_cap_pp`, {
      exclusiveMin: true,
    })
    if (item.impact.unit !== 'expected_score_percentage_points') {
      errors.push(`${impactPath}.unit must be expected_score_percentage_points`)
    }
  }
  const practicePath = `${path}.practice`
  if (validateShape(item.practice, practicePath, errors, ['available'], [
    'action_label',
    'objective',
    'payload',
    'reason',
  ])) {
    pushBoolean(errors, item.practice.available, `${practicePath}.available`)
    pushNullableString(errors, item.practice.action_label, `${practicePath}.action_label`)
    pushNullableString(errors, item.practice.objective, `${practicePath}.objective`)
    pushNullableString(errors, item.practice.reason, `${practicePath}.reason`)
    if (item.practice.payload != null && !isRecord(item.practice.payload)) {
      errors.push(`${practicePath}.payload must be an object or null`)
    }
    if (item.practice.available === true && (
      !isNonEmptyString(item.practice.action_label)
      || !isNonEmptyString(item.practice.objective)
    )) {
      errors.push(`${practicePath} available noticed practice needs its action and objective`)
    }
    if (item.practice.available === false && !isNonEmptyString(item.practice.reason)) {
      errors.push(`${practicePath} absent noticed practice must name its reason`)
    }
  }
  if (!isRecord(item.provenance)) errors.push(`${path}.provenance must be an object`)
  pushRequiredString(errors, item.module_version, `${path}.module_version`)
}

function validateConversionLedger(ledger, path, errors) {
  const required = [
    'module_version',
    'phase_definition_version',
    'games_reaching_endgame',
    'window_games',
    'families',
  ]
  if (!validateShape(ledger, path, errors, required)) return
  pushRequiredString(errors, ledger.module_version, `${path}.module_version`)
  pushRequiredString(errors, ledger.phase_definition_version, `${path}.phase_definition_version`)
  pushInteger(errors, ledger.games_reaching_endgame, `${path}.games_reaching_endgame`, { min: 1 })
  pushInteger(errors, ledger.window_games, `${path}.window_games`, { min: 1 })
  if (!isRecord(ledger.families) || Object.keys(ledger.families).length === 0) {
    errors.push(`${path}.families must be a non-empty family table`)
    return
  }
  let coveredGames = 0
  for (const [family, row] of Object.entries(ledger.families)) {
    const familyPath = `${path}.families.${family}`
    if (!validateShape(row, familyPath, errors, ['games', 'expected_points_held_pp', 'game_ids'])) continue
    pushInteger(errors, row.games, `${familyPath}.games`, { min: 1 })
    if (!Number.isFinite(row.expected_points_held_pp)) {
      errors.push(`${familyPath}.expected_points_held_pp must be a number`)
    }
    validateStringArray(row.game_ids, `${familyPath}.game_ids`, errors, { unique: true })
    if (Array.isArray(row.game_ids) && new Set(row.game_ids).size !== row.games) {
      errors.push(`${familyPath}.game_ids must match the family game count`)
    }
    if (Number.isInteger(row.games)) coveredGames += row.games
  }
  if (Number.isInteger(ledger.games_reaching_endgame) && coveredGames !== ledger.games_reaching_endgame) {
    errors.push(`${path}.families must cover exactly the endgame games`)
  }
  if (
    Number.isInteger(ledger.games_reaching_endgame)
    && Number.isInteger(ledger.window_games)
    && ledger.games_reaching_endgame > ledger.window_games
  ) {
    errors.push(`${path}.games_reaching_endgame cannot exceed the window`)
  }
}

function validateAspectSection(section, name, errors) {
  const path = `aspect_sections.${name}`
  const required = ['aspect', 'state', 'reason', 'recommendations', 'noticed', 'expected_points']
  const optional = ['opening_table', 'conversion_ledger']
  if (!validateShape(section, path, errors, required, optional)) return

  pushEnum(errors, section.aspect, ASPECT_SET, `${path}.aspect`)
  if (section.aspect !== name) errors.push(`${path}.aspect must be ${name}`)
  pushEnum(errors, section.state, SECTION_STATES, `${path}.state`)
  pushNullableString(errors, section.reason, `${path}.reason`)

  if (!Array.isArray(section.recommendations)) {
    errors.push(`${path}.recommendations must be an array`)
  } else {
    if (section.recommendations.length > 2) {
      errors.push(`${path}.recommendations cannot contain more than two items`)
    }
    section.recommendations.forEach((recommendation, index) => {
      validateRecommendation(recommendation, index, `${path}.recommendations[${index}]`, errors)
    })
    // One honest state per section: gated recommendations force "diagnosis";
    // "diagnosis" without a recommendation is a zero-filled shell.
    if (section.state === 'diagnosis' && section.recommendations.length === 0) {
      errors.push(`${path} a diagnosis section must carry at least one recommendation`)
    }
    if (section.state !== 'diagnosis' && section.recommendations.length > 0) {
      errors.push(`${path} gated recommendations force the diagnosis state`)
    }
  }

  if (!Array.isArray(section.noticed)) {
    errors.push(`${path}.noticed must be an array`)
  } else {
    section.noticed.forEach((item, index) => {
      validateNoticedInsight(item, name, `${path}.noticed[${index}]`, errors)
    })
  }

  if (section.state === 'insufficient_data') {
    if (!isNonEmptyString(section.reason)) {
      errors.push(`${path} insufficient data must name its reason`)
    }
    if (Array.isArray(section.noticed) && section.noticed.length > 0) {
      errors.push(`${path} an insufficient-data section cannot carry noticed claims`)
    }
  }

  if (section.expected_points != null) {
    validateAspectExpectedPoints(section.expected_points, `${path}.expected_points`, errors)
  }

  if (hasOwn(section, 'opening_table') && section.opening_table != null) {
    if (name !== 'openings') {
      errors.push(`${path}.opening_table belongs to the openings section`)
    } else if (!Array.isArray(section.opening_table) || section.opening_table.length === 0) {
      errors.push(`${path}.opening_table must be a non-empty array or null`)
    } else {
      section.opening_table.forEach((row, index) => (
        validateOpeningTableRow(row, `${path}.opening_table[${index}]`, errors)
      ))
    }
  }
  if (hasOwn(section, 'conversion_ledger') && section.conversion_ledger != null) {
    if (name !== 'endgame') {
      errors.push(`${path}.conversion_ledger belongs to the endgame section`)
    } else {
      validateConversionLedger(section.conversion_ledger, `${path}.conversion_ledger`, errors)
    }
  }
}

function validateAspectHeadline(headline, errors) {
  const path = 'headline'
  if (!validateShape(headline, path, errors, ['aspect', 'recommendation_id'])) return
  pushEnum(errors, headline.aspect, ASPECT_SET, `${path}.aspect`)
  pushRequiredString(errors, headline.recommendation_id, `${path}.recommendation_id`)
}

/** Resolve the headline recommendation, or null when nothing earned it. */
export function headlineRecommendation(report) {
  const headline = report?.headline
  if (!isRecord(headline) || !ASPECT_SET.has(headline.aspect)) return null
  const section = report?.aspect_sections?.[headline.aspect]
  const recommendations = Array.isArray(section?.recommendations) ? section.recommendations : []
  return recommendations.find((item) => item?.id === headline.recommendation_id) || null
}

/** The four sections in contract order, as `{ aspect, section }` entries. */
export function orderedAspectSections(report) {
  return ASPECT_ORDER
    .map((aspect) => ({ aspect, section: report?.aspect_sections?.[aspect] }))
    .filter(({ section }) => isRecord(section))
}

/**
 * Every gated recommendation in one presentation order: the headline
 * recommendation first (it owns the first viewport), then the remaining
 * section recommendations in aspect order.
 */
export function allRecommendations(report) {
  const headline = headlineRecommendation(report)
  const rest = orderedAspectSections(report).flatMap(({ section }) => (
    (Array.isArray(section.recommendations) ? section.recommendations : []).filter(isRecord)
  ))
  if (!headline) return rest
  return [headline, ...rest.filter((item) => item !== headline)]
}

const RUN_DIFF_STATES = new Set([
  'improved',
  'unchanged',
  'worse',
  'not_tested',
  'no_new_games',
  'not_comparable_enumerator_changed',
])

function validateSinceLastScan(diff, errors) {
  const path = 'since_last_scan'
  const required = [
    'policy_version',
    'principle_id',
    'principle_label',
    'enumerator_version',
    'state',
    'source',
    'current',
    'source_run_id',
    'current_run_id',
  ]
  if (!validateShape(diff, path, errors, required)) return
  if (diff.policy_version !== 'run-diff-v1') {
    errors.push(`${path}.policy_version must be "run-diff-v1"`)
  }
  pushRequiredString(errors, diff.principle_id, `${path}.principle_id`)
  pushRequiredString(errors, diff.principle_label, `${path}.principle_label`)
  pushRequiredString(errors, diff.enumerator_version, `${path}.enumerator_version`)
  pushEnum(errors, diff.state, RUN_DIFF_STATES, `${path}.state`)
  if (validateShape(diff.source, `${path}.source`, errors, ['game_count', 'faced', 'missed'])) {
    pushInteger(errors, diff.source.game_count, `${path}.source.game_count`, { min: 1 })
    pushInteger(errors, diff.source.faced, `${path}.source.faced`, { min: 1 })
    pushInteger(errors, diff.source.missed, `${path}.source.missed`, { min: 0 })
  }
  if ((diff.current == null) !== (diff.state === 'not_comparable_enumerator_changed')) {
    errors.push(`${path}.current may be null only for a non-comparable diff`)
  }
  if (diff.current != null) {
    const currentPath = `${path}.current`
    if (validateShape(diff.current, currentPath, errors, ['new_game_count', 'faced', 'found', 'missed'])) {
      pushInteger(errors, diff.current.new_game_count, `${currentPath}.new_game_count`, { min: 0 })
      pushInteger(errors, diff.current.faced, `${currentPath}.faced`, { min: 0 })
      pushInteger(errors, diff.current.found, `${currentPath}.found`, { min: 0 })
      pushInteger(errors, diff.current.missed, `${currentPath}.missed`, { min: 0 })
      if (diff.current.found + diff.current.missed !== diff.current.faced) {
        errors.push(`${currentPath}.faced must equal found + missed exactly`)
      }
    }
  }
  pushRequiredString(errors, diff.source_run_id, `${path}.source_run_id`)
  pushRequiredString(errors, diff.current_run_id, `${path}.current_run_id`)
}

function validateSupportingFacts(facts, errors) {
  const path = 'supporting_facts'
  if (!validateShape(facts, path, errors, ['rating_trajectory', 'worst_openings_by_color'])) return
  if (facts.rating_trajectory != null) validateRatingTrajectory(facts.rating_trajectory, errors)
  if (!Array.isArray(facts.worst_openings_by_color)) {
    errors.push(`${path}.worst_openings_by_color must be an array`)
  } else {
    if (facts.worst_openings_by_color.length > 2) {
      errors.push(`${path}.worst_openings_by_color cannot exceed two colors`)
    }
    facts.worst_openings_by_color.forEach((opening, index) => (
      validateOpeningFact(opening, index, errors)
    ))
    const colors = facts.worst_openings_by_color.map((opening) => opening?.color)
    if (new Set(colors).size !== colors.length) {
      errors.push(`${path}.worst_openings_by_color may contain one row per color`)
    }
  }
}

function validateGameQualityRow(row, index, errors) {
  const path = `secondary_details.game_quality.per_game[${index}]`
  if (!validateShape(row, path, errors, ['game_id', 'result', 'color', 'acpl', 'eval_cleanliness'])) return
  if (typeof row.game_id !== 'string') errors.push(`${path}.game_id must be a string`)
  if (typeof row.result !== 'string') errors.push(`${path}.result must be a string`)
  pushEnum(errors, row.color, new Set(['white', 'black']), `${path}.color`)
  pushBoundedNumber(errors, row.acpl, `${path}.acpl`, { nullable: true })
  pushBoundedNumber(errors, row.eval_cleanliness, `${path}.eval_cleanliness`, { nullable: true, max: 1 })
}

function validateGameQuality(quality, errors) {
  const path = 'secondary_details.game_quality'
  const required = ['per_game', 'avg_acpl', 'avg_eval_cleanliness', 'games_with_quality']
  if (!validateShape(quality, path, errors, required)) return
  if (!Array.isArray(quality.per_game)) {
    errors.push(`${path}.per_game must be an array`)
  } else {
    quality.per_game.forEach((row, index) => validateGameQualityRow(row, index, errors))
  }
  pushBoundedNumber(errors, quality.avg_acpl, `${path}.avg_acpl`, { nullable: true })
  pushBoundedNumber(errors, quality.avg_eval_cleanliness, `${path}.avg_eval_cleanliness`, {
    nullable: true,
    max: 1,
  })
  pushInteger(errors, quality.games_with_quality, `${path}.games_with_quality`, { min: 0 })
  if (Array.isArray(quality.per_game)) {
    const ids = quality.per_game.map((row) => row?.game_id)
    if (new Set(ids).size !== ids.length) errors.push(`${path}.per_game must have unique game IDs`)
    const acpls = quality.per_game.map((row) => row?.acpl).filter(Number.isFinite)
    const cleanliness = quality.per_game
      .map((row) => row?.eval_cleanliness)
      .filter(Number.isFinite)
    if (Number.isInteger(quality.games_with_quality) && quality.games_with_quality !== acpls.length) {
      errors.push(`${path}.games_with_quality must equal rows with ACPL evidence`)
    }
    const expectedAcpl = acpls.length
      ? Math.round((acpls.reduce((sum, value) => sum + value, 0) / acpls.length) * 100) / 100
      : null
    const expectedCleanliness = cleanliness.length
      ? Math.round((cleanliness.reduce((sum, value) => sum + value, 0) / cleanliness.length) * 1000) / 1000
      : null
    if (quality.avg_acpl !== expectedAcpl) {
      errors.push(`${path}.avg_acpl must equal the exact game rows`)
    }
    if (quality.avg_eval_cleanliness !== expectedCleanliness) {
      errors.push(`${path}.avg_eval_cleanliness must equal the exact game rows`)
    }
  }
}

function validateSecondaryDetails(details, errors) {
  const path = 'secondary_details'
  if (!validateShape(details, path, errors, [], ['game_quality', 'coarse_unique_choice_count'])) return
  if (hasOwn(details, 'game_quality') && details.game_quality != null) {
    validateGameQuality(details.game_quality, errors)
  }
  if (hasOwn(details, 'coarse_unique_choice_count')) {
    pushInteger(errors, details.coarse_unique_choice_count, `${path}.coarse_unique_choice_count`, {
      nullable: true,
      min: 0,
    })
  }
}

function timestampMillis(value, path, errors, { nullable = false } = {}) {
  if (nullable && value == null) return null
  if (!isNonEmptyString(value) || !Number.isFinite(Date.parse(value))) {
    errors.push(`${path} must be an ISO timestamp${nullable ? ' or null' : ''}`)
    return null
  }
  return Date.parse(value)
}

function validateDelivery(delivery, diagnosisState, errors) {
  const path = 'delivery'
  const required = [
    'state',
    'content_state',
    'evidence_updated_at',
    'checked_at',
    'fresh_until',
    'refresh_available',
    'refresh_in_progress',
    'refresh_outcome',
    'previous_report_preserved',
    'next_action',
    'message',
    'failure_reason',
  ]
  if (!validateShape(delivery, path, errors, required)) return
  pushEnum(errors, delivery.state, DELIVERY_STATES, `${path}.state`)
  pushEnum(errors, delivery.content_state, DIAGNOSIS_STATES, `${path}.content_state`)
  if (delivery.content_state !== diagnosisState) {
    errors.push(`${path}.content_state must match diagnosis_state`)
  }
  const evidenceUpdatedAt = timestampMillis(
    delivery.evidence_updated_at,
    `${path}.evidence_updated_at`,
    errors,
  )
  const checkedAt = timestampMillis(delivery.checked_at, `${path}.checked_at`, errors)
  const freshUntil = timestampMillis(
    delivery.fresh_until,
    `${path}.fresh_until`,
    errors,
    { nullable: true },
  )
  pushBoolean(errors, delivery.refresh_available, `${path}.refresh_available`)
  pushBoolean(errors, delivery.refresh_in_progress, `${path}.refresh_in_progress`)
  pushBoolean(errors, delivery.previous_report_preserved, `${path}.previous_report_preserved`)
  if (delivery.refresh_outcome != null) {
    pushEnum(errors, delivery.refresh_outcome, REFRESH_OUTCOMES, `${path}.refresh_outcome`)
  }
  pushEnum(errors, delivery.next_action, DELIVERY_ACTIONS, `${path}.next_action`)
  pushNullableString(errors, delivery.message, `${path}.message`)
  pushNullableString(errors, delivery.failure_reason, `${path}.failure_reason`)

  if (evidenceUpdatedAt != null && checkedAt != null && checkedAt < evidenceUpdatedAt) {
    errors.push(`${path}.checked_at cannot predate displayed evidence`)
  }
  if (DIAGNOSIS_STATES.has(delivery.state) && delivery.state !== delivery.content_state) {
    errors.push(`${path} current state must equal content_state`)
  }
  if (delivery.state === 'stale' || delivery.state === 'failed') {
    if (delivery.previous_report_preserved !== true || delivery.fresh_until != null) {
      errors.push(`${path} stale/failed state must preserve and label previous evidence`)
    }
  } else if (freshUntil == null || (checkedAt != null && freshUntil < checkedAt)) {
    errors.push(`${path} current state needs a non-expired freshness boundary`)
  }
  if (delivery.refresh_in_progress !== (delivery.state === 'stale')) {
    errors.push(`${path}.refresh_in_progress must identify only stale active refreshes`)
  }
  if (delivery.state === 'stale' && delivery.next_action !== 'observe_refresh') {
    errors.push(`${path} stale state must observe the active refresh`)
  }
  if (delivery.state === 'failed') {
    if (!isNonEmptyString(delivery.failure_reason)) {
      errors.push(`${path} failed state requires a named failure`)
    }
    const expected = delivery.refresh_available ? 'retry_refresh' : 'retry_report'
    if (delivery.next_action !== expected) {
      errors.push(`${path} failed state must expose the permitted retry`)
    }
  } else if (delivery.failure_reason != null) {
    errors.push(`${path} only failed state may contain a failure reason`)
  }
  if (
    (delivery.next_action === 'check_fresh_games' || delivery.next_action === 'retry_refresh')
    && delivery.refresh_available !== true
  ) {
    errors.push(`${path} signed-in refresh actions require refresh availability`)
  }
  if (delivery.next_action === 'retry_report' && delivery.refresh_available === true) {
    errors.push(`${path}.retry_report is only the non-refresh fallback`)
  }
  if (delivery.refresh_outcome === 'no_new_games') {
    if (
      delivery.state === 'stale'
      || delivery.state === 'failed'
      || delivery.previous_report_preserved !== true
    ) {
      errors.push(`${path} no-new-games must preserve a successful previous report`)
    }
    if (delivery.next_action !== 'play_more_games') {
      errors.push(`${path} no-new-games must ask for new evidence`)
    }
  }
}

/**
 * Validate the complete public diagnosis-v3 boundary. The key allowlists and
 * invariants intentionally mirror backend ``DiagnosisReport``: browser mocks
 * and production responses fail on the same accidental or legacy fields.
 */
export function diagnosisReportErrors(report) {
  const errors = []
  const required = [
    'schema_version',
    'analysis_version',
    'analysis_profile_hash',
    'header',
    'diagnosis_state',
    'diagnosis_reason',
    'delivery',
    'window',
    'rating_context',
    'headline',
    'aspect_sections',
    'headline_stat',
    'identity_artifact',
    'supporting_facts',
    'observations',
    'secondary_details',
  ]
  // since_last_scan is ephemeral read-time state, like delivery.
  const optional = ['since_last_scan']
  if (!validateShape(report, 'report', errors, required, optional)) return errors

  if (report.schema_version !== DIAGNOSIS_SCHEMA_VERSION) {
    errors.push(`schema_version must be ${DIAGNOSIS_SCHEMA_VERSION}`)
  }
  pushInteger(errors, report.analysis_version, 'analysis_version', { min: 1 })
  pushRequiredString(errors, report.analysis_profile_hash, 'analysis_profile_hash')
  validateHeader(report.header, errors)
  pushEnum(errors, report.diagnosis_state, DIAGNOSIS_STATES, 'diagnosis_state')
  pushNullableString(errors, report.diagnosis_reason, 'diagnosis_reason')
  validateDelivery(report.delivery, report.diagnosis_state, errors)
  validateWindow(report.window, errors)
  validateRatingContext(report.rating_context, errors)

  const sectionsValid = validateShape(
    report.aspect_sections,
    'aspect_sections',
    errors,
    ASPECT_ORDER,
  )
  if (sectionsValid) {
    for (const aspect of ASPECT_ORDER) {
      validateAspectSection(report.aspect_sections[aspect], aspect, errors)
    }
  }

  const sectionRecommendations = sectionsValid
    ? ASPECT_ORDER.flatMap((aspect) => {
        const items = report.aspect_sections[aspect]?.recommendations
        return Array.isArray(items) ? items.filter(isRecord) : []
      })
    : []
  const recommendationIds = new Set()
  for (const recommendation of sectionRecommendations) {
    if (recommendationIds.has(recommendation.id)) {
      errors.push(`recommendation id ${recommendation.id} must be unique in the report`)
    }
    recommendationIds.add(recommendation.id)
  }

  if (report.diagnosis_state === 'complete' && sectionsValid && sectionRecommendations.length === 0) {
    errors.push('a complete diagnosis must contain at least one recommendation')
  }
  if (report.diagnosis_state !== 'complete' && !isNonEmptyString(report.diagnosis_reason)) {
    errors.push('non-complete diagnosis requires a reason')
  }
  if (report.diagnosis_state === 'complete' && report.diagnosis_reason != null) {
    errors.push('a complete diagnosis cannot carry an incompleteness reason')
  }

  // The single cross-aspect Aha: required exactly when a recommendation
  // exists, and it must resolve to a real section recommendation.
  if (report.headline != null) validateAspectHeadline(report.headline, errors)
  if (sectionsValid) {
    if (sectionRecommendations.length > 0 && report.headline == null) {
      errors.push('a report with recommendations must name its headline')
    }
    if (report.headline != null && headlineRecommendation(report) == null) {
      errors.push('the headline must reference an existing section recommendation')
    }
  }

  validateHeadline(report.headline_stat, errors)
  if (report.identity_artifact != null) validateIdentityArtifact(report.identity_artifact, errors)
  validateSupportingFacts(report.supporting_facts, errors)
  if (hasOwn(report, 'since_last_scan') && report.since_last_scan != null) {
    validateSinceLastScan(report.since_last_scan, errors)
  }
  if (report.observations != null) {
    validateObservations(report.observations, errors)
    const ledger = report.observations.was_winning_ledger
    if (
      Array.isArray(ledger)
      && Number.isInteger(report.headline_stat?.was_winning)
      && ledger.length !== report.headline_stat.was_winning
    ) {
      errors.push('observations.was_winning_ledger must list exactly the headline games')
    }
    const collapse = report.observations.collapse_by_phase
    if (
      collapse != null
      && Number.isInteger(report.window?.losses)
      && collapse.opening + collapse.middlegame + collapse.endgame > report.window.losses
    ) {
      errors.push('observations.collapse_by_phase cannot exceed the window losses')
    }
    if (
      report.observations.conversion != null
      && Number.isInteger(report.window?.games_analyzed)
      && report.observations.conversion.winning_positions > report.window.games_analyzed
    ) {
      errors.push('observations.conversion cannot exceed the report window')
    }
    if (
      report.observations.expected_points != null
      && Number.isInteger(report.window?.games_analyzed)
      && report.observations.expected_points.games_with_events > report.window.games_analyzed
    ) {
      errors.push('observations.expected_points cannot exceed the report window')
    }
  }
  validateSecondaryDetails(report.secondary_details, errors)
  if (report.header?.rating !== report.rating_context?.current_rating) {
    errors.push('header and rating_context must identify the same rating')
  }
  if (report.headline_stat?.total_losses !== report.window?.losses) {
    errors.push('headline loss denominator must equal the report window')
  }

  const reportEventIds = new Set()
  const maiaIdentities = new Set()
  for (const recommendation of sectionRecommendations) {
    if (recommendation?.recurrence?.window_games !== report.window?.games_analyzed) {
      errors.push(`recommendation ${recommendation.id} window_games must equal the report window`)
    }
    // Event-id disjointness holds across ALL sections, not per section: one
    // decision event proves at most one claim anywhere in the report.
    for (const eventId of recommendation?.recurrence?.event_ids || []) {
      if (reportEventIds.has(eventId)) {
        errors.push('one decision event cannot support multiple recommendations')
      }
      reportEventIds.add(eventId)
    }
    if (report.diagnosis_state === 'partial' && recommendation?.practice?.ready === true) {
      errors.push('a partial diagnosis cannot advertise practice readiness')
    }
    for (const block of [recommendation?.findability, recommendation?.practice?.difficulty_context]) {
      if (isRecord(block) && block.source === 'maia3') {
        maiaIdentities.add(`${block.model_version}:${block.behavior_profile_hash}`)
      }
    }
  }
  // F10: no pinned-constant comparison, but one document may not mix
  // behavior identities — every modeled claim shares one recorded model.
  if (maiaIdentities.size > 1) {
    errors.push('the report must carry one consistent Maia-3 behavior identity')
  }

  if (report.diagnosis_state === 'partial' && report.identity_artifact != null) {
    errors.push('a partial diagnosis cannot publish an identity artifact')
  }
  if (report.diagnosis_state !== 'partial' && report.identity_artifact == null) {
    errors.push('a complete analysis requires its portable identity artifact')
  }
  const headlineRec = sectionsValid ? headlineRecommendation(report) : null
  if (report.identity_artifact != null) {
    if (report.identity_artifact.recommendation_id !== (headlineRec?.id ?? null)) {
      errors.push('identity artifact must bridge to the headline recommendation')
    }
    const expectedBridge = headlineRec
      ? (headlineRec.practice?.ready === true && headlineRec.practice?.action_label
          ? headlineRec.practice.action_label
          : headlineRec.carry_rule)
      : ARCHETYPE_CATALOG[report.identity_artifact.key]?.practice_bridge
    if (report.identity_artifact.practice_bridge !== expectedBridge) {
      errors.push('identity artifact must use the headline recommendation practice action')
    }
  }

  if (sectionsValid && Number.isInteger(report.window?.games_analyzed)) {
    const windowGames = report.window.games_analyzed
    for (const aspect of ASPECT_ORDER) {
      const section = report.aspect_sections[aspect]
      if (!isRecord(section)) continue
      if (
        isRecord(section.expected_points)
        && section.expected_points.games_with_events > windowGames
      ) {
        errors.push(`aspect_sections.${aspect}.expected_points cannot exceed the report window`)
      }
      for (const noticed of Array.isArray(section.noticed) ? section.noticed : []) {
        if ((noticed?.recurrence_game_ids?.length ?? 0) > windowGames) {
          errors.push(`aspect_sections.${aspect} noticed recurrence cannot exceed the report window`)
        }
      }
      if (Array.isArray(section.opening_table) && section.opening_table.some((row) => (
        row?.game_count > windowGames
      ))) {
        errors.push('opening table rows cannot exceed the report window')
      }
    }
  }

  const trajectory = report.supporting_facts?.rating_trajectory
  if (trajectory != null && trajectory.game_count > report.window?.games_analyzed) {
    errors.push('rating trajectory cannot exceed the report window')
  }
  if (trajectory != null && trajectory.end_rating !== report.header?.rating) {
    errors.push('rating trajectory must end at the report rating')
  }
  if (
    report.supporting_facts?.worst_openings_by_color?.some((opening) => (
      opening?.game_count > report.window?.games_analyzed
    ))
  ) {
    errors.push('opening support cannot exceed the report window')
  }
  if (
    Array.isArray(report.secondary_details?.game_quality?.per_game)
    && Number.isInteger(report.window?.games_analyzed)
    && report.secondary_details.game_quality.per_game.length > report.window.games_analyzed
  ) {
    errors.push('game-quality rows cannot exceed the report window')
  }
  return errors
}

export function assertDiagnosisReport(report) {
  const errors = diagnosisReportErrors(report)
  if (errors.length) {
    throw new Error(`Invalid diagnosis report: ${errors.join('; ')}`)
  }
  return report
}
